import React, { useState, useEffect, useMemo } from "react";
import { useHistory } from "react-router-dom";
import { Modal, ModalBody, Input, FormGroup, Label, Row, Col, Button, Badge } from "reactstrap";
import { ClipLoader } from "react-spinners";

import api from "../../../services/api";
import { useCompanies } from "../../../hooks/companies";
import { buildRecalculoPresetFromTable, buildRecalculoPreviewRequestFromTable } from "./recalculoImpacto";
import { formatDateLabel, toDateInputValue } from "./dateUtils";
import AnexosTabela from "./AnexosTabela";

// ─── helpers ──────────────────────────────────────────────────────────────────

const inputStyle = {
  background: "var(--everlog-card-surface-field)",
  border: "1px solid var(--everlog-card-border)",
  color: "var(--everlog-card-title)",
};

const labelStyle = { fontSize: "0.82rem", color: "var(--everlog-card-muted)", marginBottom: "0.25rem" };
const thS = { color: "var(--everlog-table-head-text)", borderColor: "var(--everlog-card-border)", fontSize: "0.78rem", textTransform: "uppercase", padding: "0.5rem 0.5rem", background: "var(--everlog-table-head-bg)", position: "sticky", top: 0 };
const tdS = { color: "var(--everlog-card-title)", borderColor: "var(--everlog-card-border)", fontSize: "0.83rem", padding: "0.4rem 0.5rem", verticalAlign: "middle", background: "var(--everlog-card-surface-field)" };
const helperTextStyle = { color: "var(--everlog-card-muted)", fontSize: "0.76rem", marginTop: "0.2rem" };
const errorTextStyle = { color: "var(--everlog-status-danger)", fontSize: "0.76rem", marginTop: "0.2rem" };
const numberCellStyle = { ...tdS, textAlign: "right", fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" };
const selectedGroupCellStyle = {
  background: "var(--theme-accent)",
  color: "var(--everlog-on-accent-text)",
  borderColor: "var(--theme-accent-border)",
  fontWeight: 600,
};
const selectedGroupNumberCellStyle = {
  ...selectedGroupCellStyle,
  textAlign: "right",
  fontVariantNumeric: "tabular-nums",
  whiteSpace: "nowrap",
};
const groupValueBadgeStyle = {
  display: "inline-flex",
  alignItems: "center",
  minHeight: 29,
  padding: "0 0.65rem",
  borderRadius: 6,
  fontSize: "0.72rem",
  fontWeight: 700,
  lineHeight: 1,
  letterSpacing: "0.02em",
};

function getInputStyle(hasError) {
  return hasError ? { ...inputStyle, border: "1px solid var(--everlog-status-danger)" } : inputStyle;
}

function findOptionLabel(options, value, fallback = "-") {
  const item = (options || []).find((option) => String(option.value) === String(value));
  return item?.label || fallback;
}

function formatNumericLabel(value) {
  if (value == null || value === "") return "-";
  const num = Number(value);
  if (Number.isNaN(num)) return String(value);
  return num.toLocaleString("pt-BR", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 4,
  });
}

// PO1-3148: modo da linha de custo (Fixo / Excedente / Por unidade sobre o total).
// O armazenamento é por linha: `adicional` (bit) + `divisorUnidade` (PO1-3145).
const MODO_CUSTO_OPTIONS = [
  { value: "fixo", label: "Fixo" },
  { value: "excedente", label: "Excedente" },
  { value: "por_unidade_total", label: "Por unidade sobre o total" },
];
const MODO_CUSTO_LABEL = { fixo: "Fixo", excedente: "Excedente", por_unidade_total: "Por unid. total" };
const MODO_CUSTO_BADGE = { fixo: "secondary", excedente: "info", por_unidade_total: "success" };

function resolveModoCusto(row) {
  if (row && Number(row.divisorUnidade) > 0) return "por_unidade_total";
  return row && row.adicional ? "excedente" : "fixo";
}

// PO1-3916: unidade "PcFx" = percentual sobre o valor da NF, por faixa de
// valor da NF. A faixa (valUnidadeInicial/Final) é em R$ e o "custo" é o %.
// Só cabe o modo Fixo (não há excedente nem divisor de peso) e não há cubagem.
const UNIDADE_PCFX = "PcFx";

function codigoUnidadeDe(drops, idFreteUnidade) {
  const unidades = (drops && drops.unidades) || [];
  const u = unidades.find((x) => String(x.value) === String(idFreteUnidade));
  return (u && u.codigo) || "";
}

// Rótulos dos campos de faixa/custo da aba Custos conforme a unidade.
function rotulosCusto(unidadeCodigo) {
  if (unidadeCodigo === UNIDADE_PCFX) {
    return {
      inicial: "Valor da NF de (R$)", final: "Valor da NF até (R$)", custo: "Percentual (%)",
      colInicial: "NF de (R$)", colFinal: "NF até (R$)", colCusto: "Percentual (%)",
    };
  }
  return {
    inicial: "Unid. Inicial", final: "Unid. Final", custo: "Custo",
    colInicial: "Unid. Ini.", colFinal: "Unid. Fim", colCusto: "Custo",
  };
}

// Equivalente por kg com CINCO casas (336,96 / 1000 = 0,33696). Função própria de
// propósito: formatTaxaValueLabel corta em 4 e também formata a aba de Taxas.
function formatCustoPorKgLabel(custo, divisor) {
  const c = Number(custo);
  const d = Number(divisor);
  if (!Number.isFinite(c) || !Number.isFinite(d) || d <= 0) return "-";
  return (c / d).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 5 });
}

function formatTaxaValueLabel(value) {
  if (value == null || value === "") return "-";
  const num = Number(value);
  if (Number.isNaN(num)) return String(value);
  return num.toLocaleString("pt-BR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 4,
  });
}

function formatCepLabel(value) {
  const digits = String(value || "").replace(/\D/g, "");
  if (!digits) return "-";
  const cep = digits.padStart(8, "0").slice(-8);
  return cep.replace(/(\d{5})(\d{3})/, "$1-$2");
}

function formatCepInput(value) {
  const digits = String(value || "").replace(/\D/g, "").slice(0, 8);
  if (!digits) return "";
  if (digits.length <= 5) return digits;
  return digits.replace(/(\d{5})(\d{0,3})/, "$1-$2");
}

function getCepDigits(value) {
  return String(value || "").replace(/\D/g, "").slice(0, 8);
}

// Busca as faixas de CEP da Cidade e retorna o envelope (menor inicial / maior final).
// Usado para autopreencher CEP Inicial/Final ao escolher a Cidade nos forms de Custos e Taxas.
async function fetchFaixaCepEnvelopeForCidade(idCidade) {
  if (!idCidade) return null;
  try {
    const res = await api.get(`/frete/cidades/${idCidade}/faixas-cep`);
    const rows = Array.isArray(res.data) ? res.data : [];
    if (!rows.length) return null;
    let minIni = null;
    let maxFim = null;
    rows.forEach((row) => {
      const ini = Number(row.cepInicial);
      const fim = Number(row.cepFinal);
      if (!Number.isNaN(ini) && (minIni === null || ini < minIni)) minIni = ini;
      if (!Number.isNaN(fim) && (maxFim === null || fim > maxFim)) maxFim = fim;
    });
    if (minIni === null || maxFim === null) return null;
    return {
      cepInicial: formatCepInput(String(minIni).padStart(8, "0")),
      cepFinal: formatCepInput(String(maxFim).padStart(8, "0")),
      faixasCount: rows.length,
    };
  } catch {
    return null;
  }
}

function normalizeGroupNumber(value) {
  if (value === undefined || value === null || value === "") return null;
  const normalized = Number(String(value).replace(",", "."));
  return Number.isNaN(normalized) ? null : normalized;
}

function buildTaxaGroupCriteria(group = {}) {
  return {
    idFreteTaxa: group?.idFreteTaxa ? Number(group.idFreteTaxa) : null,
    valorRegra: normalizeGroupNumber(group?.valorRegra),
    valor: normalizeGroupNumber(group?.valor),
    custoMinimo: normalizeGroupNumber(group?.custoMinimo),
    custoMaximo: normalizeGroupNumber(group?.custoMaximo),
  };
}

function isSameTaxaGroup(left, right) {
  const a = buildTaxaGroupCriteria(left);
  const b = buildTaxaGroupCriteria(right);

  return a.idFreteTaxa === b.idFreteTaxa
    && a.valorRegra === b.valorRegra
    && a.valor === b.valor
    && a.custoMinimo === b.custoMinimo
    && a.custoMaximo === b.custoMaximo;
}

function ValidationMessage({ text }) {
  if (!text) return null;
  return <div style={errorTextStyle}>{text}</div>;
}

function NoticeBanner({ notice }) {
  if (!notice?.message) return null;
  const success = notice.type === "success";
  return (
    <div
      style={{
        background: success ? "var(--everlog-status-success-bg)" : "var(--everlog-status-danger-bg)",
        border: `1px solid ${success ? "var(--everlog-status-success-border)" : "var(--everlog-status-danger-border)"}`,
        color: success ? "var(--everlog-status-success)" : "var(--everlog-status-danger)",
        borderRadius: 8,
        padding: "0.75rem 0.9rem",
        marginBottom: "0.9rem",
        fontSize: "0.84rem",
      }}
    >
      {notice.message}
    </div>
  );
}

function ConfirmDialog({ isOpen, title, message, confirmLabel, confirmColor = "danger", onCancel, onConfirm }) {
  return (
    <Modal isOpen={isOpen} toggle={onCancel} fade={false} modalClassName="modal-align-top">
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        background: "var(--everlog-card-surface-flat)", borderBottom: "1px solid var(--everlog-card-border)", padding: "16px 20px",
      }}>
        <span style={{ color: "var(--everlog-card-title)", fontWeight: 600, fontSize: 15 }}>{title}</span>
        <button
          onClick={onCancel}
          style={{ background: "none", border: "none", color: "var(--everlog-card-title)", fontSize: 22, lineHeight: 1, cursor: "pointer", padding: "0 4px" }}
          aria-label="Fechar"
        >
          ×
        </button>
      </div>
      <ModalBody>
        <p style={{ color: "var(--everlog-card-text)", fontSize: "0.9rem", marginBottom: "1rem" }}>{message}</p>
        <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.5rem" }}>
          <Button color="secondary" size="sm" onClick={onCancel}>Cancelar</Button>
          <Button color={confirmColor} size="sm" onClick={onConfirm}>{confirmLabel}</Button>
        </div>
      </ModalBody>
    </Modal>
  );
}

function ContextSummary({ form, drops }) {
  const summaryItems = [
    { label: "Modal", value: findOptionLabel(drops.modais, form.idModal) },
    { label: "Frete", value: form.servico || "-" },
    { label: "Tipo de Serviço", value: findOptionLabel(drops.tiposServico, form.idTipoServico) },
    { label: "Tipo de tabela", value: findOptionLabel(drops.tiposFrete, form.idTipoFrete) },
    { label: "Unidade", value: findOptionLabel(drops.unidades, form.idFreteUnidade) },
    { label: "Negócio", value: findOptionLabel(drops.negocios, form.idNegocio) },
    { label: "Vigência", value: `${formatDateLabel(form.dataValIni)} até ${formatDateLabel(form.dataValFim)}` },
  ];

  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
        gap: "0.65rem 0.9rem",
        marginBottom: "1rem",
        padding: "0.9rem 1rem",
        borderRadius: 10,
        border: "1px solid var(--everlog-card-border)",
        background: "var(--everlog-card-surface-muted)",
      }}
    >
      {summaryItems.map((item) => (
        <div key={item.label}>
          <div style={{ color: "var(--everlog-card-muted)", fontSize: "0.72rem", marginBottom: "0.2rem", textTransform: "uppercase", letterSpacing: "0.04em" }}>
            {item.label}
          </div>
          <div style={{ color: "var(--everlog-card-title)", fontSize: "0.84rem" }}>{item.value}</div>
        </div>
      ))}
    </div>
  );
}

function Sel({ label, value, onChange, options, placeholder = "Selecione...", required, error, helperText }) {
  return (
    <FormGroup style={{ marginBottom: "0.75rem" }}>
      <Label style={labelStyle}>{label}{required && <span style={{ color: "var(--everlog-status-danger)" }}> *</span>}</Label>
      <Input type="select" value={value} onChange={(e) => onChange(e.target.value)} style={getInputStyle(error)}>
        <option value="">{placeholder}</option>
        {options.map((o) => <option key={o.value} value={o.value} disabled={Boolean(o.disabled)}>{o.label}</option>)}
      </Input>
      {helperText ? <div style={helperTextStyle}>{helperText}</div> : null}
      <ValidationMessage text={error} />
    </FormGroup>
  );
}

function Txt({ label, value, onChange, placeholder, required, type = "text", error, helperText, min, disabled }) {
  return (
    <FormGroup style={{ marginBottom: "0.75rem" }}>
      <Label style={labelStyle}>{label}{required && <span style={{ color: "var(--everlog-status-danger)" }}> *</span>}</Label>
      <Input type={type} value={value} onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder} style={getInputStyle(error)} min={min} disabled={disabled} />
      {helperText ? <div style={helperTextStyle}>{helperText}</div> : null}
      <ValidationMessage text={error} />
    </FormGroup>
  );
}

function Radio({ label, value, onChange, options, disabled, helperText }) {
  return (
    <FormGroup style={{ marginBottom: "0.75rem" }}>
      <Label style={labelStyle}>{label}</Label>
      <div style={{ display: "flex", gap: "0.5rem", marginTop: "0.25rem", flexWrap: "wrap" }}>
        {options.map((o) => (
          <button
            key={o.value}
            type="button"
            disabled={disabled}
            onClick={() => !disabled && onChange(o.value)}
            style={{
              opacity: disabled ? 0.6 : 1,
              cursor: disabled ? "not-allowed" : "pointer",
              background: value === o.value ? "var(--theme-accent-glow)" : "var(--everlog-card-surface-field)",
              border: `1px solid ${value === o.value ? "var(--theme-accent)" : "var(--everlog-card-border)"}`,
              color: value === o.value ? "var(--everlog-card-title)" : "var(--everlog-card-muted)",
              fontSize: "0.84rem",
              borderRadius: 10,
              padding: "0.45rem 0.8rem",
              minWidth: 86,
            }}
          >
            {o.label}
          </button>
        ))}
      </div>
      {helperText ? <div style={{ fontSize: "0.78rem", color: "var(--everlog-card-muted)", marginTop: "0.25rem" }}>{helperText}</div> : null}
    </FormGroup>
  );
}

// ─── Tab: Dados Principais ────────────────────────────────────────────────────

function TabDados({ form, setForm, drops, estados, cidades, onEstadoChange, errors, aguardandoAprovacao }) {
  const freteUnidadeCodigo = codigoUnidadeDe(drops, form.idFreteUnidade);
  const isFxVc = freteUnidadeCodigo === "FxVc";
  const hasSavedFatorCubagem = form.fatorCubagem !== "" && form.fatorCubagem != null;
  // PO1-3916: PcFx (percentual sobre a NF) fica de fora — não há cubagem.
  const showFatorCubagem = ["FxVc", "FxNF", "KG"].includes(freteUnidadeCodigo) || hasSavedFatorCubagem;

  return (
    <div>
      <Row>
        <Col md="8">
          <Txt label="Nome" value={form.nome} onChange={(v) => setForm((p) => ({ ...p, nome: v }))} required error={errors.nome} />
        </Col>
        <Col md="4">
          <Radio
            label="Status"
            value={form.status}
            onChange={(v) => setForm((p) => ({ ...p, status: v }))}
            options={[
              { value: "true", label: "Ativo" },
              { value: "false", label: "Inativo" },
            ]}
            // PO1-2312: tabela vinda da transportadora só ativa pelo "Aprovar e ativar" da lista.
            disabled={Boolean(aguardandoAprovacao)}
            helperText={aguardandoAprovacao ? 'Aguardando aprovação do embarcador: ative pelo botão "Aprovar e ativar" na lista.' : null}
          />
        </Col>
      </Row>
      <Row>
        <Col md="6">
          <Sel label="Unidade" value={form.idFreteUnidade}
            onChange={(v) => setForm((p) => ({ ...p, idFreteUnidade: v }))}
            options={drops.unidades} required placeholder="Selecione a unidade..." error={errors.idFreteUnidade} />
        </Col>
        <Col md="6">
          <Sel label="Modal" value={form.idModal}
            onChange={(v) => setForm((p) => ({ ...p, idModal: v }))}
            options={drops.modais} required error={errors.idModal} />
        </Col>
      </Row>
      <Row>
        <Col md="6">
          <Sel label="Negócio" value={form.idNegocio}
            onChange={(v) => setForm((p) => ({ ...p, idNegocio: v }))}
            options={drops.negocios} required error={errors.idNegocio} />
        </Col>
        <Col md="6">
          <Sel label="Tipo de Serviço" value={form.idTipoServico}
            onChange={(v) => setForm((p) => ({ ...p, idTipoServico: v }))}
            options={drops.tiposServico} />
        </Col>
      </Row>
      <Row>
        <Col md="6">
          <Sel label="Tipo de tabela" value={form.idTipoFrete}
            onChange={(v) => setForm((p) => ({ ...p, idTipoFrete: v }))}
            options={drops.tiposFrete} />
        </Col>
        {isFxVc && (
          <Col md="6">
            <Sel label="Tipo de Veículo" value={form.idTipoVeiculo}
              onChange={(v) => setForm((p) => ({ ...p, idTipoVeiculo: v }))}
              options={drops.tiposVeiculo} />
          </Col>
        )}
        {showFatorCubagem && (
          <Col md="6">
            <Txt label="Fator de Cubagem" type="number" value={form.fatorCubagem}
              onChange={(v) => setForm((p) => ({ ...p, fatorCubagem: v }))} placeholder="0.000" />
          </Col>
        )}
      </Row>
      <Row>
        <Col md="6">
          <Sel label="UF Origem" value={form.idEstadoOrigem}
            onChange={(v) => { setForm((p) => ({ ...p, idEstadoOrigem: v, idCidadeOrigem: "" })); onEstadoChange(v, "origem"); }}
            options={estados} />
        </Col>
        <Col md="6">
          <Sel label="Cidade Origem" value={form.idCidadeOrigem}
            onChange={(v) => setForm((p) => ({ ...p, idCidadeOrigem: v }))}
            options={cidades.origem || []} placeholder="Todas as cidades" />
        </Col>
      </Row>
      <Row>
        <Col md="6">
          <Txt label="Data Validade Inicial" type="date" value={form.dataValIni}
            onChange={(v) => setForm((p) => ({ ...p, dataValIni: v }))} error={errors.dataValIni} />
        </Col>
        <Col md="6">
          <Txt label="Data Validade Final" type="date" value={form.dataValFim}
            onChange={(v) => setForm((p) => ({ ...p, dataValFim: v }))} error={errors.dataValFim} helperText="A data final deve ser igual ou posterior à inicial." />
        </Col>
      </Row>
      <Row>
        <Col md="4">
          <Radio label="Frete" value={form.servico}
            onChange={(v) => setForm((p) => ({ ...p, servico: v }))}
            options={[{ value: "Entrega", label: "Entrega" }, { value: "Coleta", label: "Coleta" }]} />
        </Col>
        <Col md="4">
          <Radio label="Incide Imposto" value={form.incideImposto}
            onChange={(v) => setForm((p) => ({ ...p, incideImposto: v }))}
            options={[{ value: "true", label: "Sim" }, { value: "false", label: "Não" }]} />
        </Col>
        <Col md="4">
          <Radio label="Frete Reverso" value={form.freteReverso}
            onChange={(v) => setForm((p) => ({ ...p, freteReverso: v }))}
            options={[{ value: "true", label: "Sim" }, { value: "false", label: "Não" }]} />
        </Col>
      </Row>
    </div>
  );
}

// ─── Tab: Custos ──────────────────────────────────────────────────────────────

function TabCustos({ tabelaId, estados, cidades, onEstadoChange, onTotalChange, unidadeCodigo = "" }) {
  const LIMIT = 25;

  const EMPTY_FORM = {
    idEstadoDestino: "", idCidadeDestino: "", cepInicial: "", cepFinal: "",
    valUnidadeInicial: "", valUnidadeFinal: "", adicional: "false",
    modo: "fixo", divisorUnidade: "",
    custo: "", custoMinimo: "", codigoSKU: "",
  };

  // PO1-3916: PcFx = faixa em R$ de NF e custo em %; só o modo Fixo.
  const isPcFx = unidadeCodigo === UNIDADE_PCFX;
  const rotulos = rotulosCusto(unidadeCodigo);
  const modoOptions = MODO_CUSTO_OPTIONS.map((o) => ({ ...o, disabled: isPcFx && o.value !== "fixo" }));

  const [viewMode, setViewMode] = useState("matrix");

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(false);
  const [linesLoaded, setLinesLoaded] = useState(false);

  const [grades, setGrades] = useState([]);
  const [gradesLoading, setGradesLoading] = useState(false);
  const [selectedGradeKey, setSelectedGradeKey] = useState("");
  const [selectedGrade, setSelectedGrade] = useState(null);
  const [totalCostRows, setTotalCostRows] = useState(0);

  const [matrixData, setMatrixData] = useState({ columns: [], rows: [], summary: null });
  const [matrixLoading, setMatrixLoading] = useState(false);
  const [matrixCidade, setMatrixCidade] = useState("");
  const [matrixEstado, setMatrixEstado] = useState("");
  const [matrixSku, setMatrixSku] = useState("");
  const [matrixOnlyAdditional, setMatrixOnlyAdditional] = useState(false);

  // Edição inline de célula na matriz agrupada por tarifa.
  // editingCell = { groupKey, colKey } | null
  const [editingCell, setEditingCell] = useState(null);
  const [cellValue, setCellValue] = useState("");
  const [cellSaving, setCellSaving] = useState(false);
  const [cellError, setCellError] = useState("");

  const [form, setForm] = useState(EMPTY_FORM);
  const [adding, setAdding] = useState(false);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [busca, setBusca] = useState("");
  const [filtroEstado, setFiltroEstado] = useState("");
  const [notice, setNotice] = useState(null);
  const [errors, setErrors] = useState({});
  const [pendingDeleteId, setPendingDeleteId] = useState(null);

  async function loadLines(off = 0, overrides = {}) {
    if (!tabelaId) return;
    setLoading(true);
    const cidadeFiltro = Object.prototype.hasOwnProperty.call(overrides, "cidade") ? overrides.cidade : busca;
    const estadoFiltro = Object.prototype.hasOwnProperty.call(overrides, "idEstadoDestino") ? overrides.idEstadoDestino : filtroEstado;
    try {
      const res = await api.get(`/frete/tabelas/${tabelaId}/custos`, {
        params: {
          limit: LIMIT,
          offset: off,
          cidade: cidadeFiltro || undefined,
          idEstadoDestino: estadoFiltro || undefined,
        },
      });
      setRows(res.data.rows || []);
      setTotal(res.data.total || 0);
      setOffset(off);
      setLinesLoaded(true);
    } catch (error) {
      setNotice({ type: "error", message: error.response?.data?.error || error.message || "Erro ao carregar custos." });
    } finally {
      setLoading(false);
    }
  }

  async function loadMatrix(groupKey, overrides = {}, gradeOverride = null) {
    if (!tabelaId || !groupKey) {
      setMatrixData({ columns: [], rows: [], summary: null });
      return;
    }

    setMatrixLoading(true);
    const cidadeFiltro = Object.prototype.hasOwnProperty.call(overrides, "cidade") ? overrides.cidade : matrixCidade;
    const estadoFiltro = Object.prototype.hasOwnProperty.call(overrides, "idEstadoDestino") ? overrides.idEstadoDestino : matrixEstado;
    const skuFiltro = Object.prototype.hasOwnProperty.call(overrides, "codigoSKU") ? overrides.codigoSKU : matrixSku;
    const adicionalFiltro = Object.prototype.hasOwnProperty.call(overrides, "adicional") ? overrides.adicional : matrixOnlyAdditional;

    try {
      const res = await api.get(`/frete/tabelas/${tabelaId}/custos-matriz`, {
        params: {
          groupKey,
          cidade: cidadeFiltro || undefined,
          idEstadoDestino: estadoFiltro || undefined,
          codigoSKU: skuFiltro || undefined,
          adicional: adicionalFiltro ? "true" : undefined,
        },
      });
      setMatrixData({
        columns: res.data.columns || [],
        rows: res.data.rows || [],
        summary: res.data.summary || null,
      });
      if (gradeOverride) {
        setSelectedGrade(gradeOverride);
      }
    } catch (error) {
      setMatrixData({ columns: [], rows: [], summary: null });
      setNotice({ type: "error", message: error.response?.data?.error || error.message || "Erro ao carregar matriz de custos." });
    } finally {
      setMatrixLoading(false);
    }
  }

  async function loadGrades(preferredGroupKey = null) {
    if (!tabelaId) return;
    setGradesLoading(true);
    try {
      const res = await api.get(`/frete/tabelas/${tabelaId}/custos-grades`);
      const nextGrades = res.data.rows || [];
      const nextTotalRows = res.data.totalRows || 0;
      setGrades(nextGrades);
      setTotalCostRows(nextTotalRows);
      if (onTotalChange) onTotalChange(nextTotalRows);

      if (!nextGrades.length) {
        setSelectedGradeKey("");
        setSelectedGrade(null);
        setMatrixData({ columns: [], rows: [], summary: null });
        return;
      }

      const candidateKey = preferredGroupKey || selectedGradeKey;
      const nextSelected = nextGrades.find((grade) => grade.groupKey === candidateKey) || nextGrades[0];

      setSelectedGradeKey(nextSelected.groupKey);
      setSelectedGrade(nextSelected);
      await loadMatrix(nextSelected.groupKey, {}, nextSelected);
    } catch (error) {
      setNotice({ type: "error", message: error.response?.data?.error || error.message || "Erro ao carregar grades de custos." });
    } finally {
      setGradesLoading(false);
    }
  }

  useEffect(() => {
    setViewMode("matrix");
    setRows([]);
    setTotal(0);
    setOffset(0);
    setLinesLoaded(false);
    setGrades([]);
    setSelectedGradeKey("");
    setSelectedGrade(null);
    setTotalCostRows(0);
    setMatrixData({ columns: [], rows: [], summary: null });
    setMatrixCidade("");
    setMatrixEstado("");
    setMatrixSku("");
    setMatrixOnlyAdditional(false);
    setForm(EMPTY_FORM);
    setAdding(false);
    setEditId(null);
    setErrors({});
    loadGrades();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabelaId]);

  useEffect(() => {
    if (viewMode === "rows" && tabelaId && !linesLoaded) {
      loadLines(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewMode, tabelaId, linesLoaded]);

  // PO1-3916: em PcFx o modo é sempre Fixo (inclusive ao editar uma linha
  // antiga gravada com outro modo).
  useEffect(() => {
    if (isPcFx && form.modo !== "fixo") {
      setForm((p) => ({ ...p, modo: "fixo", adicional: "false", divisorUnidade: "" }));
    }
  }, [isPcFx, form.modo]);

  function handleEdit(r) {
    setViewMode("rows");
    setEditId(r.id);
    setForm({
      idEstadoDestino: String(r.idEstadoDestino || ""),
      idCidadeDestino: String(r.idCidadeDestino || ""),
      cepInicial: formatCepInput(r.cepInicial),
      cepFinal: formatCepInput(r.cepFinal),
      valUnidadeInicial: r.valUnidadeInicial != null ? String(r.valUnidadeInicial) : "",
      valUnidadeFinal: r.valUnidadeFinal != null ? String(r.valUnidadeFinal) : "",
      adicional: r.adicional ? "true" : "false",
      modo: resolveModoCusto(r),
      divisorUnidade: Number(r.divisorUnidade) > 0 ? String(r.divisorUnidade) : "",
      custo: r.custo != null ? String(r.custo) : "",
      custoMinimo: r.custoMinimo != null ? String(r.custoMinimo) : "",
      codigoSKU: r.codigoSKU || "",
    });
    if (r.idEstadoDestino) onEstadoChange(String(r.idEstadoDestino), "custo");
    setErrors({});
    setNotice(null);
    setAdding(true);
  }

  function handleStartLineCreate() {
    setViewMode("rows");
    setEditId(null);
    setForm(EMPTY_FORM);
    setErrors({});
    setNotice(null);
    setAdding(true);
  }

  function handleCancel() {
    setAdding(false);
    setEditId(null);
    setForm(EMPTY_FORM);
    setErrors({});
    setNotice(null);
  }

  function validateForm() {
    const nextErrors = {};
    if (!form.idEstadoDestino) nextErrors.idEstadoDestino = "Selecione a UF de destino.";
    if (form.custo === "" || form.custo == null) nextErrors.custo = "Informe o custo.";
    if (form.modo === "por_unidade_total") {
      const d = Number(String(form.divisorUnidade).replace(",", "."));
      if (!Number.isFinite(d) || d <= 0) nextErrors.divisorUnidade = "Informe o divisor (ex.: 1000 para R$ por tonelada).";
    }
    if (
      form.valUnidadeInicial !== "" &&
      form.valUnidadeFinal !== "" &&
      Number(form.valUnidadeFinal) < Number(form.valUnidadeInicial)
    ) {
      nextErrors.valUnidadeFinal = "A unidade final deve ser maior ou igual à inicial.";
    }
    if (
      form.cepInicial &&
      form.cepFinal &&
      Number(getCepDigits(form.cepFinal)) < Number(getCepDigits(form.cepInicial))
    ) {
      nextErrors.cepFinal = "O CEP final deve ser maior ou igual ao inicial.";
    }
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  }

  async function handleSave() {
    if (!validateForm()) return;
    setSaving(true);
    try {
      const payload = {
        ...form,
        idCidadeDestino: form.idCidadeDestino || null,
        cepInicial: getCepDigits(form.cepInicial) || null,
        cepFinal: getCepDigits(form.cepFinal) || null,
        valUnidadeInicial: form.valUnidadeInicial || 0,
        valUnidadeFinal: form.valUnidadeFinal || 0,
        // PO1-3148: o modo vira o par (adicional, divisorUnidade) do contrato.
        adicional: form.modo === "excedente",
        divisorUnidade: form.modo === "por_unidade_total"
          ? Number(String(form.divisorUnidade).replace(",", "."))
          : null,
        custoMinimo: form.custoMinimo || null,
        codigoSKU: form.codigoSKU || null,
      };
      delete payload.modo;
      if (editId) {
        await api.put(`/frete/tabelas/${tabelaId}/custos/${editId}`, payload);
        setNotice({ type: "success", message: "Custo atualizado com sucesso." });
      } else {
        await api.post(`/frete/tabelas/${tabelaId}/custos`, payload);
        setNotice({ type: "success", message: "Custo adicionado com sucesso." });
      }
      setForm(EMPTY_FORM);
      setAdding(false);
      setEditId(null);
      setErrors({});
      await Promise.all([
        loadLines(editId ? offset : 0),
        loadGrades(selectedGradeKey),
      ]);
    } catch (error) {
      setNotice({ type: "error", message: error.response?.data?.error || error.message || "Erro ao salvar custo." });
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    if (!pendingDeleteId) return;
    try {
      await api.delete(`/frete/tabelas/${tabelaId}/custos/${pendingDeleteId}`);
      setNotice({ type: "success", message: "Custo removido com sucesso." });
      await Promise.all([
        loadLines(offset),
        loadGrades(selectedGradeKey),
      ]);
    } catch (error) {
      setNotice({ type: "error", message: error.response?.data?.error || error.message || "Erro ao remover custo." });
    } finally {
      setPendingDeleteId(null);
    }
  }

  function handleSelectGrade(grade) {
    setSelectedGradeKey(grade.groupKey);
    setSelectedGrade(grade);
    loadMatrix(grade.groupKey, {}, grade);
  }

  function handleStartEditCell(group, colKey) {
    const cellInfo = group.cellsByCol[colKey];
    if (!cellInfo || !cellInfo.ids.length) return;
    setCellError("");
    setCellValue(cellInfo.sample != null && cellInfo.sample.custo != null ? String(cellInfo.sample.custo).replace(".", ",") : "");
    setEditingCell({ groupKey: group.key, colKey });
  }

  function handleCancelEditCell() {
    setEditingCell(null);
    setCellValue("");
    setCellError("");
  }

  // Salva o custo da célula para TODOS os destinos do grupo (mesma tarifa).
  async function handleSaveEditCell(group, colKey) {
    const cellInfo = group.cellsByCol[colKey];
    const ids = cellInfo ? cellInfo.ids : [];

    if (!ids.length) {
      handleCancelEditCell();
      return;
    }

    if (cellValue === "" || cellValue == null) {
      setCellError("Informe o valor.");
      return;
    }

    const parsedValor = normalizeGroupNumber(cellValue);
    if (parsedValor === null) {
      setCellError("Valor inválido.");
      return;
    }
    if (parsedValor < 0) {
      setCellError("O valor não pode ser negativo.");
      return;
    }

    setCellSaving(true);
    setCellError("");

    try {
      const res = await api.put(`/frete/tabelas/${tabelaId}/custos-massa`, {
        ids,
        modo: "absoluto",
        valor: cellValue,
      });

      const updatedCount = (res.data && res.data.updatedCount) || 0;
      if (!updatedCount) {
        setNotice({ type: "error", message: "Nenhum destino encontrado para atualizar." });
        return;
      }

      setNotice({ type: "success", message: `Custo atualizado em ${updatedCount} destino${updatedCount !== 1 ? "s" : ""} desta tarifa.` });
      handleCancelEditCell();
      await loadMatrix(selectedGradeKey);
      if (linesLoaded) {
        loadLines(offset);
      }
    } catch (error) {
      const msg = (error.response && error.response.data && error.response.data.error) || error.message || "Erro ao atualizar o custo.";
      setCellError(msg);
    } finally {
      setCellSaving(false);
    }
  }

  const totalPages = Math.ceil(total / LIMIT);
  const currentPage = Math.floor(offset / LIMIT) + 1;
  const summaryText = viewMode === "matrix"
    ? `${grades.length} grade${grades.length !== 1 ? "s" : ""} / ${totalCostRows} custo${totalCostRows !== 1 ? "s" : ""}`
    : totalCostRows && total !== totalCostRows
      ? `${total} de ${totalCostRows} custo${totalCostRows !== 1 ? "s" : ""}`
      : `${total} custo${total !== 1 ? "s" : ""}`;
  // Agrupa as rotas da matriz por tarifa idêntica (mesmo vetor de custos por faixa).
  // Cada grupo guarda, por coluna, a lista de ids das células — para editar uma vez
  // e aplicar a todos os destinos da tarifa.
  const groupedMatrixRows = useMemo(() => {
    const cols = matrixData.columns || [];
    const groupsMap = new Map();

    (matrixData.rows || []).forEach((row) => {
      const signature = cols
        .map((column) => {
          const cell = row.cells ? row.cells[column.key] : null;
          if (!cell) return "x";
          return `${cell.custo == null ? "n" : cell.custo}:${cell.custoMinimo == null ? "n" : cell.custoMinimo}`;
        })
        .join("|");

      if (!groupsMap.has(signature)) groupsMap.set(signature, []);
      groupsMap.get(signature).push(row);
    });

    let index = 0;
    return Array.from(groupsMap.entries()).map(([signature, groupRows]) => {
      const cellsByCol = {};
      cols.forEach((column) => {
        const ids = [];
        let sample = null;
        groupRows.forEach((row) => {
          const cell = row.cells ? row.cells[column.key] : null;
          if (cell && cell.id != null) {
            ids.push(cell.id);
            if (!sample) sample = cell;
          }
        });
        cellsByCol[column.key] = { ids, sample };
      });

      index += 1;
      return {
        key: `grp-${index}-${signature}`,
        destinos: groupRows,
        cellsByCol,
      };
    });
  }, [matrixData]);

  return (
    <div>
      <ConfirmDialog
        isOpen={!!pendingDeleteId}
        title="Excluir custo"
        message="Este custo será removido da tabela de frete."
        confirmLabel="Excluir custo"
        onCancel={() => setPendingDeleteId(null)}
        onConfirm={handleDelete}
      />

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "0.75rem", flexWrap: "wrap", marginBottom: "0.75rem" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", flexWrap: "wrap" }}>
          <span style={{ color: "var(--everlog-card-muted)", fontSize: "0.85rem" }}>{summaryText}</span>
          <div style={{ display: "inline-flex", border: "1px solid var(--everlog-card-border)", borderRadius: 10, overflow: "hidden", background: "var(--everlog-card-surface-flat)" }}>
            {[
              { value: "matrix", label: "Matriz" },
              { value: "rows", label: "Linhas" },
            ].map((mode) => (
              <button
                key={mode.value}
                type="button"
                onClick={() => setViewMode(mode.value)}
                style={{
                  background: viewMode === mode.value ? "var(--theme-accent-glow)" : "transparent",
                  border: "none",
                  borderRight: mode.value === "matrix" ? "1px solid var(--everlog-card-border)" : "none",
                  color: viewMode === mode.value ? "var(--everlog-card-title)" : "var(--everlog-card-muted)",
                  fontSize: "0.84rem",
                  fontWeight: viewMode === mode.value ? 600 : 400,
                  padding: "0.45rem 0.8rem",
                  minWidth: 100,
                }}
              >
                {mode.label}
              </button>
            ))}
          </div>
        </div>

        {viewMode === "matrix" ? (
          <Button size="sm" color="info" outline onClick={handleStartLineCreate}>
            <i className="tim-icons icon-simple-add" /> Adicionar em Linhas
          </Button>
        ) : (
          <Button size="sm" color="info" onClick={() => { setEditId(null); setForm(EMPTY_FORM); setAdding((v) => !v); }}>
            <i className="tim-icons icon-simple-add" /> Adicionar
          </Button>
        )}
      </div>

      <NoticeBanner notice={notice} />

      {viewMode === "matrix" ? (
        <>
          <div style={{ color: "var(--everlog-card-muted)", fontSize: "0.82rem", marginBottom: "0.75rem" }}>
            As faixas podem variar por tabela e transportadora. Selecione uma grade para visualizar a matriz correspondente.
          </div>

          {gradesLoading ? (
            <div style={{ textAlign: "center", padding: "1rem" }}><ClipLoader size={40} color="var(--theme-accent)" /></div>
          ) : grades.length === 0 ? (
            <div style={{ ...tdS, textAlign: "center", color: "var(--everlog-card-muted)", borderRadius: 8, border: "1px solid var(--everlog-card-border)", padding: "1rem" }}>
              Nenhuma grade de faixas encontrada para esta tabela.
            </div>
          ) : (
            <>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: "0.65rem", marginBottom: "0.85rem" }}>
                {grades.map((grade, index) => {
                  const isActive = grade.groupKey === selectedGradeKey;
                  return (
                    <button
                      key={grade.groupKey}
                      type="button"
                      onClick={() => handleSelectGrade(grade)}
                      style={{
                        background: isActive ? "var(--theme-accent-glow)" : "var(--everlog-card-surface-field)",
                        border: `1px solid ${isActive ? "var(--theme-accent)" : "var(--everlog-card-border)"}`,
                        boxShadow: isActive ? "0 0 0 1px var(--theme-accent-glow)" : "none",
                        borderRadius: 10,
                        color: "var(--everlog-card-title)",
                        padding: "0.85rem 0.95rem",
                        textAlign: "left",
                      }}
                    >
                      <div style={{ display: "flex", justifyContent: "space-between", gap: "0.5rem", marginBottom: "0.35rem" }}>
                        <span style={{ fontSize: "0.8rem", fontWeight: 600 }}>Grade {index + 1}</span>
                        {grade.temAdicional ? <Badge color="info">Tem adicional</Badge> : null}
                        {grade.temPorUnidade ? <Badge color="success" style={{ marginLeft: 4 }}>Por unid. total</Badge> : null}
                      </div>
                      <div style={{ color: "var(--everlog-card-text)", fontSize: "0.82rem", marginBottom: "0.35rem" }}>
                        {grade.qtdFaixas} faixa{grade.qtdFaixas !== 1 ? "s" : ""} · {grade.qtdRotas} rota{grade.qtdRotas !== 1 ? "s" : ""}
                      </div>
                      <div style={{ color: "var(--everlog-card-muted)", fontSize: "0.76rem", lineHeight: 1.4 }}>
                        {grade.faixasPreview || "Sem prévia"}
                        {grade.qtdFaixas > 4 ? "..." : ""}
                      </div>
                    </button>
                  );
                })}
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "220px 1fr 180px auto auto", gap: "0.5rem", marginBottom: "0.85rem" }}>
                <Input
                  type="select"
                  value={matrixEstado}
                  onChange={(e) => setMatrixEstado(e.target.value)}
                  style={{ background: "var(--everlog-card-surface-field)", border: "1px solid var(--everlog-card-border)", color: "var(--everlog-card-title)", fontSize: "0.85rem", height: 34 }}
                >
                  <option value="">Todas as UFs</option>
                  {estados.map((estado) => (
                    <option key={estado.value} value={estado.value}>{estado.label}</option>
                  ))}
                </Input>
                <Input
                  type="text"
                  placeholder="Buscar por cidade..."
                  value={matrixCidade}
                  onChange={(e) => setMatrixCidade(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && loadMatrix(selectedGradeKey)}
                  style={{ background: "var(--everlog-card-surface-field)", border: "1px solid var(--everlog-card-border)", color: "var(--everlog-card-title)", fontSize: "0.85rem", height: 34 }}
                />
                <Input
                  type="text"
                  placeholder="SKU"
                  value={matrixSku}
                  onChange={(e) => setMatrixSku(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && loadMatrix(selectedGradeKey)}
                  style={{ background: "var(--everlog-card-surface-field)", border: "1px solid var(--everlog-card-border)", color: "var(--everlog-card-title)", fontSize: "0.85rem", height: 34 }}
                />
                <Button
                  size="sm"
                  color={matrixOnlyAdditional ? "info" : "secondary"}
                  onClick={() => {
                    const nextValue = !matrixOnlyAdditional;
                    setMatrixOnlyAdditional(nextValue);
                    loadMatrix(selectedGradeKey, { adicional: nextValue });
                  }}
                >
                  {matrixOnlyAdditional ? "Só adicionais" : "Todos os custos"}
                </Button>
                <div style={{ display: "flex", gap: "0.5rem" }}>
                  <Button size="sm" color="info" style={{ whiteSpace: "nowrap" }} onClick={() => loadMatrix(selectedGradeKey)}>Buscar</Button>
                  <Button
                    size="sm"
                    color="secondary"
                    onClick={() => {
                      setMatrixCidade("");
                      setMatrixEstado("");
                      setMatrixSku("");
                      setMatrixOnlyAdditional(false);
                      loadMatrix(selectedGradeKey, { cidade: "", idEstadoDestino: "", codigoSKU: "", adicional: false });
                    }}
                  >
                    Limpar busca
                  </Button>
                </div>
              </div>

              <div style={{ background: "var(--everlog-card-surface-field)", border: "1px solid var(--everlog-card-border)", borderRadius: 10, padding: "0.85rem 0.9rem" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "0.75rem", flexWrap: "wrap", marginBottom: "0.75rem" }}>
                  <div>
                    <div style={{ color: "var(--everlog-card-muted)", fontSize: "0.7rem", textTransform: "uppercase", marginBottom: "0.15rem", letterSpacing: "0.04em" }}>
                      Matriz da grade selecionada
                    </div>
                    <div style={{ color: "var(--everlog-card-title)", fontSize: "0.88rem", fontWeight: 600 }}>
                      {selectedGrade ? `${selectedGrade.qtdFaixas} faixa${selectedGrade.qtdFaixas !== 1 ? "s" : ""} · ${selectedGrade.qtdRotas} rota${selectedGrade.qtdRotas !== 1 ? "s" : ""}` : "Selecione uma grade"}
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: "0.45rem", flexWrap: "wrap", alignItems: "center" }}>
                    {matrixData.summary ? (
                      <>
                        <Badge color="info" style={groupValueBadgeStyle}>Rotas {matrixData.summary.qtdRotas}</Badge>
                        <Badge color="secondary" style={groupValueBadgeStyle}>Faixas {matrixData.summary.qtdFaixas}</Badge>
                        <Badge color="secondary" style={groupValueBadgeStyle}>Células {matrixData.summary.qtdCelulas}</Badge>
                      </>
                    ) : null}
                    <Button size="sm" color="secondary" outline onClick={() => setViewMode("rows")}>
                      Usar visão Linhas para editar
                    </Button>
                  </div>
                </div>

                <div style={{ color: "var(--everlog-card-muted)", fontSize: "0.76rem", marginBottom: "0.6rem" }}>
                  Destinos com a mesma tarifa estão agrupados. Clique em um valor para editar — a alteração vale para todos os destinos do grupo.
                </div>

                {matrixLoading ? (
                  <div style={{ textAlign: "center", padding: "1.25rem" }}><ClipLoader size={36} color="var(--theme-accent)" /></div>
                ) : (
                  <div style={{ overflow: "auto", maxHeight: 420, border: "1px solid var(--everlog-card-border)", borderRadius: 8 }}>
                    <table className="table" style={{ marginBottom: 0, minWidth: 760 }}>
                      <thead>
                        <tr>
                          <th style={{ ...thS, minWidth: 240 }}>Destinos do grupo</th>
                          {(matrixData.columns || []).map((column) => (
                            <th key={column.key} style={{ ...thS, minWidth: 130, textAlign: "right" }}>
                              <div>{column.label}</div>
                              {column.modo === "por_unidade_total" || Number(column.divisorUnidade) > 0 ? (
                                <div style={{ color: "var(--everlog-status-success, #2ecc71)", fontSize: "0.66rem", marginTop: "0.1rem" }}>Por unid. total ÷{column.divisorUnidade}</div>
                              ) : column.adicional ? (
                                <div style={{ color: "var(--theme-accent)", fontSize: "0.66rem", marginTop: "0.1rem" }}>Adicional</div>
                              ) : null}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {groupedMatrixRows.length === 0 ? (
                          <tr>
                            <td colSpan={(matrixData.columns || []).length + 1} style={{ ...tdS, textAlign: "center", color: "var(--everlog-card-muted)" }}>
                              Nenhuma rota encontrada para a grade e filtros selecionados.
                            </td>
                          </tr>
                        ) : groupedMatrixRows.map((group) => {
                          return (
                            <tr key={group.key}>
                              <td style={{ ...tdS, minWidth: 240, verticalAlign: "top" }}>
                                <div style={{ color: "var(--theme-accent)", fontSize: "0.7rem", fontWeight: 600, marginBottom: "0.25rem" }}>
                                  {group.destinos.length} destino{group.destinos.length !== 1 ? "s" : ""}
                                </div>
                                <div style={{ maxHeight: 132, overflowY: "auto", display: "flex", flexDirection: "column", gap: "0.3rem" }}>
                                  {group.destinos.map((destino) => (
                                    <div key={destino.key}>
                                      <div style={{ color: "var(--everlog-card-title)", fontWeight: 500, fontSize: "0.82rem" }}>
                                        {destino.cidade || "Todas as cidades"} <span style={{ color: "var(--everlog-card-muted)", fontWeight: 400 }}>{destino.uf ? `(${destino.uf})` : ""}</span>
                                      </div>
                                      <div style={{ color: "var(--everlog-card-muted)", fontSize: "0.7rem" }}>
                                        {formatCepLabel(destino.cepInicial)} – {formatCepLabel(destino.cepFinal)}
                                        {destino.codigoSKU ? ` · SKU ${destino.codigoSKU}` : ""}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </td>
                              {(matrixData.columns || []).map((column) => {
                                const cellInfo = group.cellsByCol[column.key];
                                const sample = cellInfo ? cellInfo.sample : null;
                                const editable = !!(cellInfo && cellInfo.ids.length);
                                const isEditing = editingCell && editingCell.groupKey === group.key && editingCell.colKey === column.key;

                                if (isEditing) {
                                  return (
                                    <td key={`${group.key}-${column.key}`} style={{ ...numberCellStyle, minWidth: 130, verticalAlign: "top" }}>
                                      <div style={{ display: "flex", flexDirection: "column", gap: "0.3rem", alignItems: "flex-end" }}>
                                        <Input
                                          type="text"
                                          value={cellValue}
                                          autoFocus
                                          onChange={(e) => { setCellValue(e.target.value); setCellError(""); }}
                                          onKeyDown={(e) => {
                                            if (e.key === "Enter") handleSaveEditCell(group, column.key);
                                            if (e.key === "Escape") handleCancelEditCell();
                                          }}
                                          style={{ ...inputStyle, height: 30, textAlign: "right", padding: "0.2rem 0.4rem" }}
                                        />
                                        <div style={{ display: "flex", gap: "0.3rem" }}>
                                          <Button size="sm" color="success" disabled={cellSaving} onClick={() => handleSaveEditCell(group, column.key)} style={{ padding: "0.15rem 0.5rem" }}>
                                            {cellSaving ? "..." : "OK"}
                                          </Button>
                                          <Button size="sm" color="secondary" outline disabled={cellSaving} onClick={handleCancelEditCell} style={{ padding: "0.15rem 0.5rem" }}>
                                            ✕
                                          </Button>
                                        </div>
                                        {cellError ? <div style={{ color: "var(--everlog-status-danger)", fontSize: "0.66rem" }}>{cellError}</div> : null}
                                      </div>
                                    </td>
                                  );
                                }

                                return (
                                  <td
                                    key={`${group.key}-${column.key}`}
                                    onClick={editable ? () => handleStartEditCell(group, column.key) : undefined}
                                    title={editable ? "Clique para editar o custo desta tarifa" : undefined}
                                    style={{
                                      ...numberCellStyle,
                                      minWidth: 130,
                                      verticalAlign: "top",
                                      cursor: editable ? "pointer" : "default",
                                    }}
                                  >
                                    {sample ? (
                                      <>
                                        <div style={{ color: "var(--everlog-card-title)", textDecoration: editable ? "underline dotted var(--everlog-card-border)" : "none" }}>
                                          {formatTaxaValueLabel(sample.custo)}
                                        </div>
                                        {sample.custoMinimo != null && Number(sample.custoMinimo) > 0 ? (
                                          <div style={{ color: "var(--everlog-card-muted)", fontSize: "0.68rem", marginTop: "0.1rem" }}>
                                            mín. {formatTaxaValueLabel(sample.custoMinimo)}
                                          </div>
                                        ) : null}
                                      </>
                                    ) : "—"}
                                  </td>
                                );
                              })}
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </>
          )}
        </>
      ) : (
        <>
          <div style={{ display: "grid", gridTemplateColumns: "220px 1fr auto auto", gap: "0.5rem", marginBottom: "0.75rem" }}>
            <Input
              type="select"
              value={filtroEstado}
              onChange={(e) => setFiltroEstado(e.target.value)}
              style={{ background: "var(--everlog-card-surface-field)", border: "1px solid var(--everlog-card-border)", color: "var(--everlog-card-title)", fontSize: "0.85rem", height: 34 }}
            >
              <option value="">Todas as UFs</option>
              {estados.map((estado) => (
                <option key={estado.value} value={estado.value}>{estado.label}</option>
              ))}
            </Input>
            <Input
              type="text"
              placeholder="Buscar por cidade..."
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && loadLines(0)}
              style={{ background: "var(--everlog-card-surface-field)", border: "1px solid var(--everlog-card-border)", color: "var(--everlog-card-title)", fontSize: "0.85rem", height: 34 }}
            />
            <Button size="sm" color="info" style={{ whiteSpace: "nowrap" }} onClick={() => loadLines(0)}>Buscar</Button>
            <Button
              size="sm"
              color="secondary"
              onClick={() => {
                setBusca("");
                setFiltroEstado("");
                loadLines(0, { cidade: "", idEstadoDestino: "" });
              }}
            >
              Limpar busca
            </Button>
          </div>

          {adding && (
            <div style={{ background: "var(--everlog-card-surface-field)", border: `1px solid ${editId ? "var(--everlog-status-warning)" : "var(--theme-accent)"}`, boxShadow: editId ? "0 0 0 1px var(--everlog-status-warning-bg)" : "0 0 0 1px var(--theme-accent-glow)", borderRadius: 8, padding: "1rem", marginBottom: "1rem" }}>
              <div style={{ color: editId ? "var(--everlog-status-warning)" : "var(--everlog-card-muted)", fontSize: "0.78rem", fontWeight: 600, marginBottom: "0.5rem", textTransform: "uppercase" }}>
                {editId ? "Editando custo" : "Novo custo"}
              </div>
              <Row>
                <Col md="3">
                  <Sel label="UF Destino *" value={form.idEstadoDestino}
                    onChange={(v) => { setForm((p) => ({ ...p, idEstadoDestino: v, idCidadeDestino: "" })); onEstadoChange(v, "custo"); }}
                    options={estados} error={errors.idEstadoDestino} />
                </Col>
                <Col md="3">
                  <Sel label="Cidade" value={form.idCidadeDestino}
                    onChange={(v) => {
                      setForm((p) => ({ ...p, idCidadeDestino: v }));
                      if (v) {
                        fetchFaixaCepEnvelopeForCidade(v).then((envelope) => {
                          if (envelope) {
                            setForm((p) => ({ ...p, cepInicial: envelope.cepInicial, cepFinal: envelope.cepFinal }));
                          }
                        });
                      }
                    }}
                    options={cidades.custo || []} placeholder="Todas" />
                </Col>
                <Col md="3">
                  <Txt label="CEP Inicial" value={form.cepInicial} onChange={(v) => setForm((p) => ({ ...p, cepInicial: formatCepInput(v) }))} placeholder="00000-000" />
                </Col>
                <Col md="3">
                  <Txt label="CEP Final" value={form.cepFinal} onChange={(v) => setForm((p) => ({ ...p, cepFinal: formatCepInput(v) }))} placeholder="99999-999" error={errors.cepFinal} />
                </Col>
              </Row>
              <Row>
                <Col md="2">
                  <Txt label={rotulos.inicial} type="number" value={form.valUnidadeInicial} onChange={(v) => setForm((p) => ({ ...p, valUnidadeInicial: v }))} />
                </Col>
                <Col md="2">
                  <Txt label={rotulos.final} type="number" value={form.valUnidadeFinal} onChange={(v) => setForm((p) => ({ ...p, valUnidadeFinal: v }))} error={errors.valUnidadeFinal} />
                </Col>
                <Col md="2">
                  <Txt label={`${rotulos.custo} *`} type="number" value={form.custo} onChange={(v) => setForm((p) => ({ ...p, custo: v }))} error={errors.custo} />
                </Col>
                <Col md="2">
                  <Txt label="Custo Mín." type="number" value={form.custoMinimo} onChange={(v) => setForm((p) => ({ ...p, custoMinimo: v }))} />
                </Col>
                <Col md="2">
                  <Sel label="Modo" value={form.modo}
                    onChange={(v) => setForm((p) => ({ ...p, modo: v, adicional: v === "excedente" ? "true" : "false" }))}
                    options={modoOptions} placeholder="Fixo"
                    helperText={isPcFx ? "Percentual sobre o valor da NF: só o modo Fixo." : form.modo === "excedente" ? "Cobra só o peso acima do início da faixa + faixa anterior." : form.modo === "por_unidade_total" ? "Custo ÷ divisor × peso TOTAL. Diferente do \"a cada\" das taxas, que arredonda para fração cheia." : "Valor único para toda a faixa."} />
                </Col>
                <Col md="2">
                  <Txt label="SKU" value={form.codigoSKU} onChange={(v) => setForm((p) => ({ ...p, codigoSKU: v }))} />
                </Col>
              </Row>
              {form.modo === "por_unidade_total" ? (
                <Row>
                  <Col md="2">
                    <Txt label="Divisor *" type="number" value={form.divisorUnidade}
                      onChange={(v) => setForm((p) => ({ ...p, divisorUnidade: v }))}
                      error={errors.divisorUnidade} placeholder="1000" />
                  </Col>
                  <Col md="10">
                    <div style={{ ...helperTextStyle, paddingTop: "1.9rem" }}>
                      1 = por kg · 100 = por 100 kg (proporcional) · 1000 = por tonelada.
                      {form.custo && Number(form.divisorUnidade) > 0 ? (
                        <> Equivale a <strong>R$ {formatCustoPorKgLabel(form.custo, form.divisorUnidade)}/kg</strong> sobre o peso total.</>
                      ) : null}
                    </div>
                  </Col>
                </Row>
              ) : null}
              <div style={{ display: "flex", gap: "0.5rem", justifyContent: "flex-end" }}>
                <Button size="sm" color="secondary" onClick={handleCancel}>Cancelar</Button>
                <Button size="sm" color="success" onClick={handleSave} disabled={saving}>
                  {saving ? "Salvando..." : "Salvar"}
                </Button>
              </div>
            </div>
          )}

          {loading ? (
            <div style={{ textAlign: "center", padding: "1rem" }}><ClipLoader size={40} color="var(--theme-accent)" /></div>
          ) : (
            <div style={{ overflowX: "auto", maxHeight: 300 }}>
              <table className="table" style={{ marginBottom: 0 }}>
                <thead>
                  <tr>
                    <th style={thS}>UF</th><th style={thS}>Cidade</th>
                    <th style={thS}>CEP Ini.</th><th style={thS}>CEP Fim</th>
                    <th style={thS}>{rotulos.colInicial}</th><th style={thS}>{rotulos.colFinal}</th>
                    <th style={{ ...thS, textAlign: "right" }}>{rotulos.colCusto}</th><th style={{ ...thS, textAlign: "right" }}>Custo Mín.</th>
                    <th style={thS}>Modo</th><th style={{ ...thS, width: 70 }}></th>
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 ? (
                    <tr><td colSpan={10} style={{ ...tdS, textAlign: "center", color: "var(--everlog-card-muted)" }}>Nenhum custo cadastrado.</td></tr>
                  ) : rows.map((r) => (
                    <tr key={r.id}>
                      <td style={tdS}>{r.uf || "-"}</td>
                      <td style={tdS}>{r.cidade || "Todas"}</td>
                      <td style={tdS}>{formatCepLabel(r.cepInicial)}</td>
                      <td style={tdS}>{formatCepLabel(r.cepFinal)}</td>
                      <td style={numberCellStyle}>{r.valUnidadeInicial ?? "-"}</td>
                      <td style={numberCellStyle}>{r.valUnidadeFinal ?? "-"}</td>
                      <td style={numberCellStyle}>{formatTaxaValueLabel(r.custo)}</td>
                      <td style={numberCellStyle}>{r.custoMinimo != null ? formatTaxaValueLabel(r.custoMinimo) : "-"}</td>
                      <td style={tdS}>
                        <Badge color={MODO_CUSTO_BADGE[resolveModoCusto(r)]}>{MODO_CUSTO_LABEL[resolveModoCusto(r)]}</Badge>
                        {Number(r.divisorUnidade) > 0 ? (
                          <div style={{ fontSize: "0.7rem", color: "var(--everlog-card-muted)" }}>÷{r.divisorUnidade} = R$ {formatCustoPorKgLabel(r.custo, r.divisorUnidade)}/kg</div>
                        ) : null}
                      </td>
                      <td style={{ ...tdS, whiteSpace: "nowrap" }}>
                        <button className="btn btn-link btn-sm" style={{ padding: "0 4px", color: "var(--theme-accent)" }}
                          title="Editar" onClick={() => handleEdit(r)}>
                          <i className="tim-icons icon-pencil" />
                        </button>
                        <button className="btn btn-link btn-sm" style={{ padding: "0 4px", color: "var(--everlog-status-danger)" }}
                          title="Excluir" onClick={() => setPendingDeleteId(r.id)}>
                          <i className="tim-icons icon-simple-remove" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: "0.75rem" }}>
            <span style={{ color: "var(--everlog-card-muted)", fontSize: "0.8rem" }}>{total} registro{total !== 1 ? "s" : ""}</span>
            <div style={{ display: "flex", gap: "0.4rem" }}>
              <Button size="sm" color="secondary" disabled={currentPage <= 1} onClick={() => loadLines(offset - LIMIT)}>‹</Button>
              <span style={{ padding: "0.2rem 0.6rem", color: "var(--everlog-card-title)", fontSize: "0.8rem" }}>{currentPage}/{totalPages || 1}</span>
              <Button size="sm" color="secondary" disabled={currentPage >= totalPages} onClick={() => loadLines(offset + LIMIT)}>›</Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ─── Tab: Taxas ───────────────────────────────────────────────────────────────

function TabTaxas({ tabelaId, drops, estados, cidades, onEstadoChange, onTotalChange }) {
  const LIMIT = 25;
  const GROUP_LIMIT = 8;

  const [viewMode, setViewMode] = useState("grouped");

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(false);
  const [linesLoaded, setLinesLoaded] = useState(false);

  const [form, setForm] = useState({
    idFreteTaxa: "", valorRegra: "", idEstadoDestino: "", idCidadeDestino: "",
    cepInicial: "", cepFinal: "", valor: "", custoMinimo: "", custoMaximo: "",
  });
  const [adding, setAdding] = useState(false);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [busca, setBusca] = useState("");
  const [filtroEstado, setFiltroEstado] = useState("");
  const [filtroTaxa, setFiltroTaxa] = useState("");
  const [notice, setNotice] = useState(null);
  const [errors, setErrors] = useState({});
  const [pendingDeleteId, setPendingDeleteId] = useState(null);
  const EMPTY_FORM_TAXA = { idFreteTaxa: "", valorRegra: "", idEstadoDestino: "", idCidadeDestino: "", cepInicial: "", cepFinal: "", valor: "", custoMinimo: "", custoMaximo: "" };

  const [groupRows, setGroupRows] = useState([]);
  const [groupTotal, setGroupTotal] = useState(0);
  const [groupTotalRows, setGroupTotalRows] = useState(0);
  const [groupOffset, setGroupOffset] = useState(0);
  const [groupLoading, setGroupLoading] = useState(false);
  const [groupBusca, setGroupBusca] = useState("");
  const [groupFiltroTaxa, setGroupFiltroTaxa] = useState("");
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [groupRoutes, setGroupRoutes] = useState([]);
  const [groupRoutesLoading, setGroupRoutesLoading] = useState(false);
  const [groupEditing, setGroupEditing] = useState(false);
  const [groupValue, setGroupValue] = useState("");
  const [groupValueError, setGroupValueError] = useState("");
  const [groupSaving, setGroupSaving] = useState(false);

  const [taxasTiposUsados, setTaxasTiposUsados] = useState([]);

  useEffect(() => {
    if (!tabelaId) return;
    api.get(`/frete/tabelas/${tabelaId}/taxas-tipos`)
      .then((res) => setTaxasTiposUsados(Array.isArray(res.data) ? res.data : []))
      .catch(() => {});
  }, [tabelaId]);

  function refreshTaxasTipos() {
    if (!tabelaId) return;
    api.get(`/frete/tabelas/${tabelaId}/taxas-tipos`)
      .then((res) => setTaxasTiposUsados(Array.isArray(res.data) ? res.data : []))
      .catch(() => {});
  }

  const selectedTaxa = drops.taxasTipos.find((t) => String(t.value) === String(form.idFreteTaxa));
  // "A Cada" deve aparecer para qualquer taxa que tenha base variável:
  //   - Tipo ACADA       → "a cada N kg/volume" (ex: FRETE PESO FAIXA, ARMAZENAGEM POR KG)
  //   - Tipo PERCENTUAL  → "a cada N% do valor base"
  //   - Por FRETE        → casos legados em que o cálculo incide sobre o frete
  // FIXO não precisa do campo.
  const showValorRegra = selectedTaxa && (
    selectedTaxa.tipo === "ACADA" ||
    selectedTaxa.tipo === "PERCENTUAL" ||
    selectedTaxa.por === "FRETE"
  );

  async function load(off = 0, overrides = {}) {
    if (!tabelaId) return;
    setLoading(true);
    const cidadeFiltro = "cidade" in overrides ? overrides.cidade : busca;
    const estadoFiltro = "idEstadoDestino" in overrides ? overrides.idEstadoDestino : filtroEstado;
    const taxaFiltro = "idFreteTaxa" in overrides ? overrides.idFreteTaxa : filtroTaxa;
    try {
      const res = await api.get(`/frete/tabelas/${tabelaId}/taxas`, {
        params: {
          limit: LIMIT,
          offset: off,
          cidade: cidadeFiltro || undefined,
          idEstadoDestino: estadoFiltro || undefined,
          idFreteTaxa: taxaFiltro || undefined,
        },
      });
      setRows(res.data.rows || []);
      setTotal(res.data.total || 0);
      setOffset(off);
      setLinesLoaded(true);
      if (onTotalChange) onTotalChange(res.data.total || 0);
    } catch (error) {
      setNotice({ type: "error", message: error.response?.data?.error || error.message || "Erro ao carregar taxas." });
    } finally { setLoading(false); }
  }

  async function loadGroupRoutes(groupLike) {
    if (!tabelaId) return;
    const criteria = buildTaxaGroupCriteria(groupLike);
    if (!criteria.idFreteTaxa) {
      setGroupRoutes([]);
      return;
    }

    setGroupRoutesLoading(true);
    try {
      const res = await api.get(`/frete/tabelas/${tabelaId}/taxas-grupos/rotas`, {
        params: criteria,
      });
      setGroupRoutes(res.data.rows || []);
    } catch (error) {
      setGroupRoutes([]);
      setNotice({ type: "error", message: error.response?.data?.error || error.message || "Erro ao carregar rotas do grupo." });
    } finally {
      setGroupRoutesLoading(false);
    }
  }

  async function loadGrouped(off = 0, overrides = {}, selectionCriteria = null) {
    if (!tabelaId) return;
    setGroupLoading(true);
    const buscaFiltro = "busca" in overrides ? overrides.busca : groupBusca;
    const taxaFiltro = "idFreteTaxa" in overrides ? overrides.idFreteTaxa : groupFiltroTaxa;

    try {
      const res = await api.get(`/frete/tabelas/${tabelaId}/taxas-grupos`, {
        params: {
          limit: GROUP_LIMIT,
          offset: off,
          busca: buscaFiltro || undefined,
          idFreteTaxa: taxaFiltro || undefined,
        },
      });

      const nextRows = res.data.rows || [];
      setGroupRows(nextRows);
      setGroupTotal(res.data.total || 0);
      setGroupTotalRows(res.data.totalRows || 0);
      setGroupOffset(off);
      if (onTotalChange) onTotalChange(res.data.totalRows || 0);

      if (!nextRows.length) {
        setSelectedGroup(null);
        setGroupRoutes([]);
        setGroupEditing(false);
        setGroupValue("");
        setGroupValueError("");
        return;
      }

      const explicitSelection = selectionCriteria
        ? buildTaxaGroupCriteria(selectionCriteria)
        : null;

      const criteriaToSelect = explicitSelection
        || (selectedGroup ? buildTaxaGroupCriteria(selectedGroup) : null);

      const matchedRow = criteriaToSelect
        ? nextRows.find((row) => isSameTaxaGroup(row, criteriaToSelect))
        : nextRows[0];

      if (matchedRow) {
        setSelectedGroup(matchedRow);
        setGroupValue(matchedRow.valor != null ? String(matchedRow.valor) : "");
        await loadGroupRoutes(matchedRow);
      } else if (explicitSelection) {
        setSelectedGroup((prev) => ({ ...(prev || {}), ...criteriaToSelect }));
        setGroupValue(criteriaToSelect.valor != null ? String(criteriaToSelect.valor) : "");
        await loadGroupRoutes(criteriaToSelect);
      } else {
        setSelectedGroup(nextRows[0]);
        setGroupValue(nextRows[0].valor != null ? String(nextRows[0].valor) : "");
        await loadGroupRoutes(nextRows[0]);
      }
    } catch (error) {
      setNotice({ type: "error", message: error.response?.data?.error || error.message || "Erro ao carregar grupos de taxas." });
    } finally {
      setGroupLoading(false);
    }
  }

  useEffect(() => {
    setViewMode("grouped");
    setRows([]);
    setTotal(0);
    setOffset(0);
    setLinesLoaded(false);
    setGroupRows([]);
    setGroupTotal(0);
    setGroupTotalRows(0);
    setGroupOffset(0);
    setSelectedGroup(null);
    setGroupRoutes([]);
    setGroupEditing(false);
    setGroupValue("");
    setGroupValueError("");
    loadGrouped(0, {}, null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabelaId]);

  useEffect(() => {
    if (viewMode === "rows" && tabelaId && !linesLoaded) {
      load(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewMode, tabelaId, linesLoaded]);

  function handleEdit(r) {
    setViewMode("rows");
    setEditId(r.id);
    setForm({
      idFreteTaxa: String(r.idFreteTaxa || ""),
      valorRegra: r.valorRegra != null ? String(r.valorRegra) : "",
      idEstadoDestino: String(r.idEstadoDestino || ""),
      idCidadeDestino: String(r.idCidadeDestino || ""),
      cepInicial: formatCepInput(r.cepInicial),
      cepFinal: formatCepInput(r.cepFinal),
      valor: r.valor != null ? String(r.valor) : "",
      custoMinimo: r.custoMinimo != null ? String(r.custoMinimo) : "",
      custoMaximo: r.custoMaximo != null ? String(r.custoMaximo) : "",
    });
    if (r.idEstadoDestino) onEstadoChange(String(r.idEstadoDestino), "taxa");
    setErrors({});
    setNotice(null);
    setAdding(true);
  }

  function validateForm() {
    const nextErrors = {};
    if (!form.idFreteTaxa) nextErrors.idFreteTaxa = "Selecione a taxa.";
    if (form.valor === "" || form.valor == null) nextErrors.valor = "Informe o valor.";
    if (
      form.cepInicial &&
      form.cepFinal &&
      Number(getCepDigits(form.cepFinal)) < Number(getCepDigits(form.cepInicial))
    ) {
      nextErrors.cepFinal = "O CEP final deve ser maior ou igual ao inicial.";
    }
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  }

  async function handleSave() {
    if (!validateForm()) return;
    setSaving(true);
    try {
      const payload = {
        ...form,
        valorRegra: form.valorRegra || null,
        idEstadoDestino: form.idEstadoDestino || null,
        idCidadeDestino: form.idCidadeDestino || null,
        cepInicial: getCepDigits(form.cepInicial) || null,
        cepFinal: getCepDigits(form.cepFinal) || null,
        custoMinimo: form.custoMinimo || 0,
        custoMaximo: form.custoMaximo || 0,
      };
      if (editId) {
        await api.put(`/frete/tabelas/${tabelaId}/taxas/${editId}`, payload);
        setNotice({ type: "success", message: "Taxa atualizada com sucesso." });
      } else {
        await api.post(`/frete/tabelas/${tabelaId}/taxas`, payload);
        setNotice({ type: "success", message: "Taxa adicionada com sucesso." });
      }
      setForm(EMPTY_FORM_TAXA);
      setAdding(false);
      setEditId(null);
      setErrors({});
      refreshTaxasTipos();
      await Promise.all([
        load(editId ? offset : 0),
        loadGrouped(groupOffset, {}, null),
      ]);
    } catch (error) {
      setNotice({ type: "error", message: error.response?.data?.error || error.message || "Erro ao salvar taxa." });
    } finally { setSaving(false); }
  }

  function handleCancel() {
    setAdding(false);
    setEditId(null);
    setForm(EMPTY_FORM_TAXA);
    setErrors({});
    setNotice(null);
  }

  function handleStartLineCreate() {
    setViewMode("rows");
    setEditId(null);
    setForm(EMPTY_FORM_TAXA);
    setErrors({});
    setNotice(null);
    setAdding(true);
  }

  async function handleDelete() {
    if (!pendingDeleteId) return;
    try {
      await api.delete(`/frete/tabelas/${tabelaId}/taxas/${pendingDeleteId}`);
      setNotice({ type: "success", message: "Taxa removida com sucesso." });
      refreshTaxasTipos();
      await Promise.all([
        load(offset),
        loadGrouped(groupOffset, {}, null),
      ]);
    } catch (error) {
      setNotice({ type: "error", message: error.response?.data?.error || error.message || "Erro ao remover taxa." });
    } finally {
      setPendingDeleteId(null);
    }
  }

  function handleSelectGroup(group) {
    setSelectedGroup(group);
    setGroupEditing(false);
    setGroupValueError("");
    setGroupValue(group?.valor != null ? String(group.valor) : "");
    loadGroupRoutes(group);
  }

  function toggleGroupEditing() {
    if (!selectedGroup) return;
    setGroupEditing((prev) => !prev);
    setGroupValue(selectedGroup?.valor != null ? String(selectedGroup.valor) : "");
    setGroupValueError("");
  }

  async function handleSaveGroupValue() {
    if (!selectedGroup) return;

    if (groupValue === "" || groupValue == null) {
      setGroupValueError("Informe o novo valor.");
      return;
    }

    if (normalizeGroupNumber(groupValue) === null) {
      setGroupValueError("Informe um valor numérico válido.");
      return;
    }

    setGroupSaving(true);
    setGroupValueError("");

    try {
      const criteria = buildTaxaGroupCriteria(selectedGroup);
      const res = await api.put(`/frete/tabelas/${tabelaId}/taxas-grupos/valor`, {
        criteria,
        novoValor: groupValue,
      });

      const updatedCount = res.data?.updatedCount || 0;
      const nextCriteria = res.data?.criteria || { ...criteria, valor: normalizeGroupNumber(groupValue) };

      if (!updatedCount) {
        setNotice({ type: "error", message: "Nenhuma rota encontrada para atualizar esse grupo." });
        return;
      }

      setNotice({ type: "success", message: `Valor atualizado em ${updatedCount} rota${updatedCount !== 1 ? "s" : ""}.` });
      setGroupEditing(false);
      await loadGrouped(groupOffset, {}, nextCriteria);
      if (linesLoaded) {
        load(offset);
      }
    } catch (error) {
      setNotice({ type: "error", message: error.response?.data?.error || error.message || "Erro ao atualizar valor do grupo." });
    } finally {
      setGroupSaving(false);
    }
  }

  const totalPages = Math.ceil(total / LIMIT);
  const currentPage = Math.floor(offset / LIMIT) + 1;
  const groupTotalPages = Math.ceil(groupTotal / GROUP_LIMIT);
  const currentGroupPage = Math.floor(groupOffset / GROUP_LIMIT) + 1;

  const groupedSummaryText = `${groupTotal} grupo${groupTotal !== 1 ? "s" : ""} / ${groupTotalRows} rota${groupTotalRows !== 1 ? "s" : ""}`;

  return (
    <div>
      <ConfirmDialog
        isOpen={!!pendingDeleteId}
        title="Excluir taxa"
        message="Esta taxa será removida da tabela de frete."
        confirmLabel="Excluir taxa"
        onCancel={() => setPendingDeleteId(null)}
        onConfirm={handleDelete}
      />
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "0.75rem", flexWrap: "wrap", marginBottom: "0.75rem" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", flexWrap: "wrap" }}>
          <span style={{ color: "var(--everlog-card-muted)", fontSize: "0.85rem" }}>
            {viewMode === "grouped" ? groupedSummaryText : `${total} taxa${total !== 1 ? "s" : ""}`}
          </span>
          <div style={{ display: "inline-flex", border: "1px solid var(--everlog-card-border)", borderRadius: 10, overflow: "hidden", background: "var(--everlog-card-surface-flat)" }}>
            {[
              { value: "grouped", label: "Agrupadas" },
              { value: "rows", label: "Linhas" },
            ].map((mode) => (
              <button
                key={mode.value}
                type="button"
                onClick={() => setViewMode(mode.value)}
                style={{
                  background: viewMode === mode.value ? "var(--theme-accent-glow)" : "transparent",
                  border: "none",
                  borderRight: mode.value === "grouped" ? "1px solid var(--everlog-card-border)" : "none",
                  color: viewMode === mode.value ? "var(--everlog-card-title)" : "var(--everlog-card-muted)",
                  fontSize: "0.84rem",
                  fontWeight: viewMode === mode.value ? 600 : 400,
                  padding: "0.45rem 0.8rem",
                  minWidth: 110,
                }}
              >
                {mode.label}
              </button>
            ))}
          </div>
        </div>

        {viewMode === "grouped" ? (
          <Button size="sm" color="info" outline onClick={handleStartLineCreate}>
            <i className="tim-icons icon-simple-add" /> Adicionar em Linhas
          </Button>
        ) : (
          <Button size="sm" color="info" onClick={() => { setEditId(null); setForm(EMPTY_FORM_TAXA); setAdding((v) => !v); }}>
            <i className="tim-icons icon-simple-add" /> Adicionar
          </Button>
        )}
      </div>

      <NoticeBanner notice={notice} />

      {viewMode === "grouped" ? (
        <>
          <div style={{ display: "grid", gridTemplateColumns: "240px 1fr auto auto", gap: "0.5rem", marginBottom: "0.75rem" }}>
            <Input
              type="select"
              value={groupFiltroTaxa}
              onChange={(e) => setGroupFiltroTaxa(e.target.value)}
              style={{ background: "var(--everlog-card-surface-field)", border: "1px solid var(--everlog-card-border)", color: "var(--everlog-card-title)", fontSize: "0.85rem", height: 34 }}
            >
              <option value="">Todas as taxas</option>
              {taxasTiposUsados.map((taxa) => (
                <option key={taxa.value} value={taxa.value}>{taxa.label}</option>
              ))}
            </Input>
            <Input
              type="text"
              placeholder="Buscar taxa, tipo ou regra..."
              value={groupBusca}
              onChange={(e) => setGroupBusca(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && loadGrouped(0)}
              style={{ background: "var(--everlog-card-surface-field)", border: "1px solid var(--everlog-card-border)", color: "var(--everlog-card-title)", fontSize: "0.85rem", height: 34 }}
            />
            <Button size="sm" color="info" style={{ whiteSpace: "nowrap" }} onClick={() => loadGrouped(0)}>Buscar</Button>
            <Button
              size="sm"
              color="secondary"
              onClick={() => {
                setGroupBusca("");
                setGroupFiltroTaxa("");
                loadGrouped(0, { busca: "", idFreteTaxa: "" }, null);
              }}
            >
              Limpar busca
            </Button>
          </div>

          {groupLoading ? (
            <div style={{ textAlign: "center", padding: "1rem" }}><ClipLoader size={40} color="var(--theme-accent)" /></div>
          ) : (
            <div style={{ overflowX: "auto", maxHeight: 260 }}>
              <table className="table" style={{ marginBottom: 0 }}>
                <thead>
                  <tr>
                    <th style={thS}>Taxa</th>
                    <th style={thS}>Tipo</th>
                    <th style={thS}>Por</th>
                    <th style={{ ...thS, textAlign: "right" }}>A Cada</th>
                    <th style={{ ...thS, textAlign: "right" }}>Valor</th>
                    <th style={{ ...thS, textAlign: "right" }}>Custo Mín.</th>
                    <th style={{ ...thS, textAlign: "right" }}>Custo Máx.</th>
                    <th style={{ ...thS, textAlign: "right" }}>Qtd. Rotas</th>
                  </tr>
                </thead>
                <tbody>
                  {groupRows.length === 0 ? (
                    <tr><td colSpan={8} style={{ ...tdS, textAlign: "center", color: "var(--everlog-card-muted)" }}>Nenhum grupo encontrado.</td></tr>
                  ) : groupRows.map((row) => {
                    const isSelected = selectedGroup && isSameTaxaGroup(row, selectedGroup);
                    return (
                      <tr
                        key={`${row.idFreteTaxa}-${row.valor}-${row.valorRegra}-${row.custoMinimo}-${row.custoMaximo}`}
                        onClick={() => handleSelectGroup(row)}
                        style={{ cursor: "pointer" }}
                      >
                        <td style={{
                          ...(isSelected ? selectedGroupCellStyle : tdS),
                          borderLeft: isSelected ? "4px solid var(--theme-accent)" : tdS.borderLeft,
                          paddingLeft: isSelected ? "0.7rem" : tdS.padding,
                        }}>{row.freteTaxa}</td>
                        <td style={isSelected ? selectedGroupCellStyle : tdS}>{row.tipo || "-"}</td>
                        <td style={isSelected ? selectedGroupCellStyle : tdS}>{row.por || "-"}</td>
                        <td style={isSelected ? selectedGroupNumberCellStyle : numberCellStyle}>{row.valorRegra != null ? formatNumericLabel(row.valorRegra) : "-"}</td>
                        <td style={isSelected ? selectedGroupNumberCellStyle : numberCellStyle}>{formatTaxaValueLabel(row.valor)}</td>
                        <td style={isSelected ? selectedGroupNumberCellStyle : numberCellStyle}>{row.custoMinimo != null ? formatTaxaValueLabel(row.custoMinimo) : "-"}</td>
                        <td style={isSelected ? selectedGroupNumberCellStyle : numberCellStyle}>{row.custoMaximo != null ? formatTaxaValueLabel(row.custoMaximo) : "-"}</td>
                        <td style={isSelected ? selectedGroupNumberCellStyle : numberCellStyle}>{formatNumericLabel(row.qtdRotas)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: "0.75rem" }}>
            <span style={{ color: "var(--everlog-card-muted)", fontSize: "0.8rem" }}>
              {groupTotal} grupo{groupTotal !== 1 ? "s" : ""} em {groupTotalRows} rota{groupTotalRows !== 1 ? "s" : ""}
            </span>
            <div style={{ display: "flex", gap: "0.4rem" }}>
              <Button size="sm" color="secondary" disabled={currentGroupPage <= 1} onClick={() => loadGrouped(groupOffset - GROUP_LIMIT)}>‹</Button>
              <span style={{ padding: "0.2rem 0.6rem", color: "var(--everlog-card-title)", fontSize: "0.8rem" }}>{currentGroupPage}/{groupTotalPages || 1}</span>
              <Button size="sm" color="secondary" disabled={currentGroupPage >= groupTotalPages} onClick={() => loadGrouped(groupOffset + GROUP_LIMIT)}>›</Button>
            </div>
          </div>

          <div style={{ marginTop: "0.8rem", background: "var(--everlog-card-surface-field)", border: "1px solid var(--everlog-card-border)", borderRadius: 8, padding: "0.8rem 0.9rem" }}>
            {!selectedGroup ? (
              <div style={{ color: "var(--everlog-card-muted)", textAlign: "center", padding: "1rem 0" }}>
                Selecione um grupo acima para ver as rotas e editar o valor em massa.
              </div>
            ) : (
              <>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "0.65rem", flexWrap: "wrap", marginBottom: "0.7rem" }}>
                  <div>
                    <div style={{ color: "var(--everlog-card-muted)", fontSize: "0.68rem", marginBottom: "0.15rem", textTransform: "uppercase", letterSpacing: "0.04em" }}>
                      Rotas do grupo selecionado
                    </div>
                    <div style={{ color: "var(--everlog-card-title)", fontSize: "0.88rem", fontWeight: 600 }}>{selectedGroup.freteTaxa}</div>
                    <div style={{ color: "var(--everlog-card-muted)", fontSize: "0.78rem", marginTop: "0.2rem" }}>
                      {selectedGroup.tipo || "-"} · {selectedGroup.por || "-"}
                      {selectedGroup.valorRegra != null ? ` · A cada ${formatNumericLabel(selectedGroup.valorRegra)}` : ""}
                      {` · ${formatNumericLabel(selectedGroup.qtdRotas || groupRoutes.length)} rota${Number(selectedGroup.qtdRotas || groupRoutes.length) !== 1 ? "s" : ""}`}
                    </div>
                  </div>

                  <div style={{ display: "flex", gap: "0.45rem", flexWrap: "wrap", alignItems: "center" }}>
                    <Badge color="info" style={groupValueBadgeStyle}>Valor {formatTaxaValueLabel(selectedGroup.valor)}</Badge>
                    <Badge color="secondary" style={groupValueBadgeStyle}>Custo mín. {selectedGroup.custoMinimo != null ? formatTaxaValueLabel(selectedGroup.custoMinimo) : "-"}</Badge>
                    <Badge color="secondary" style={groupValueBadgeStyle}>Custo máx. {selectedGroup.custoMaximo != null ? formatTaxaValueLabel(selectedGroup.custoMaximo) : "-"}</Badge>
                    <Button size="sm" color="info" onClick={toggleGroupEditing}>
                      {groupEditing ? "Fechar edição" : "Editar valor"}
                    </Button>
                  </div>
                </div>

                {groupEditing && (
                  <div style={{ background: "var(--everlog-card-surface-flat)", border: "1px solid var(--everlog-card-border)", borderRadius: 8, padding: "0.9rem 1rem", marginBottom: "1rem" }}>
                    <Row>
                      <Col md="4">
                        <Txt
                          label="Novo valor *"
                          type="text"
                          value={groupValue}
                          onChange={setGroupValue}
                          placeholder="Ex: 9,1"
                          error={groupValueError}
                          helperText="Esta alteração afeta todas as rotas com a mesma configuração comercial."
                        />
                      </Col>
                    </Row>
                    <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.5rem" }}>
                      <Button size="sm" color="secondary" onClick={toggleGroupEditing}>Cancelar</Button>
                      <Button size="sm" color="success" onClick={handleSaveGroupValue} disabled={groupSaving}>
                        {groupSaving ? "Salvando..." : "Salvar para todas as rotas"}
                      </Button>
                    </div>
                  </div>
                )}

                {groupRoutesLoading ? (
                  <div style={{ textAlign: "center", padding: "1rem" }}><ClipLoader size={32} color="var(--theme-accent)" /></div>
                ) : (
                  <div style={{ overflowX: "auto", maxHeight: 280 }}>
                    <table className="table" style={{ marginBottom: 0 }}>
                      <thead>
                        <tr>
                          <th style={thS}>UF</th>
                          <th style={thS}>Cidade</th>
                          <th style={thS}>CEP Ini.</th>
                          <th style={thS}>CEP Fim</th>
                        </tr>
                      </thead>
                      <tbody>
                        {groupRoutes.length === 0 ? (
                          <tr><td colSpan={4} style={{ ...tdS, textAlign: "center", color: "var(--everlog-card-muted)" }}>Nenhuma rota encontrada para esse grupo.</td></tr>
                        ) : groupRoutes.map((route) => (
                          <tr key={route.id}>
                            <td style={tdS}>{route.uf || "Todos"}</td>
                            <td style={tdS}>{route.cidade || "Todas"}</td>
                            <td style={tdS}>{formatCepLabel(route.cepInicial)}</td>
                            <td style={tdS}>{formatCepLabel(route.cepFinal)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            )}
          </div>
        </>
      ) : (
        <>
          <div style={{ display: "grid", gridTemplateColumns: "220px 240px 1fr auto auto", gap: "0.5rem", marginBottom: "0.75rem" }}>
            <Input
              type="select"
              value={filtroEstado}
              onChange={(e) => setFiltroEstado(e.target.value)}
              style={{ background: "var(--everlog-card-surface-field)", border: "1px solid var(--everlog-card-border)", color: "var(--everlog-card-title)", fontSize: "0.85rem", height: 34 }}
            >
              <option value="">Todas as UFs</option>
              {estados.map((estado) => (
                <option key={estado.value} value={estado.value}>{estado.label}</option>
              ))}
            </Input>
            <Input
              type="select"
              value={filtroTaxa}
              onChange={(e) => setFiltroTaxa(e.target.value)}
              style={{ background: "var(--everlog-card-surface-field)", border: "1px solid var(--everlog-card-border)", color: "var(--everlog-card-title)", fontSize: "0.85rem", height: 34 }}
            >
              <option value="">Todas as taxas</option>
              {taxasTiposUsados.map((taxa) => (
                <option key={taxa.value} value={taxa.value}>{taxa.label}</option>
              ))}
            </Input>
            <Input
              type="text" placeholder="Buscar por cidade..." value={busca}
              onChange={(e) => setBusca(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && load(0)}
              style={{ background: "var(--everlog-card-surface-field)", border: "1px solid var(--everlog-card-border)", color: "var(--everlog-card-title)", fontSize: "0.85rem", height: 34 }}
            />
            <Button size="sm" color="info" style={{ whiteSpace: "nowrap" }} onClick={() => load(0)}>Buscar</Button>
            <Button
              size="sm"
              color="secondary"
              onClick={() => {
                setBusca("");
                setFiltroEstado("");
                setFiltroTaxa("");
                load(0, { cidade: "", idEstadoDestino: "", idFreteTaxa: "" });
              }}
            >
              Limpar busca
            </Button>
          </div>

          {adding && (
            <div style={{ background: "var(--everlog-card-surface-field)", border: `1px solid ${editId ? "var(--everlog-status-warning)" : "var(--theme-accent)"}`, boxShadow: editId ? "0 0 0 1px var(--everlog-status-warning-bg)" : "0 0 0 1px var(--theme-accent-glow)", borderRadius: 8, padding: "1rem", marginBottom: "1rem" }}>
              <div style={{ color: editId ? "var(--everlog-status-warning)" : "var(--everlog-card-muted)", fontSize: "0.78rem", fontWeight: 600, marginBottom: "0.5rem", textTransform: "uppercase" }}>
                {editId ? "Editando taxa" : "Nova taxa"}
              </div>
              <Row>
                <Col md="4">
                  <Sel label="Taxa *" value={form.idFreteTaxa}
                    onChange={(v) => setForm((p) => ({ ...p, idFreteTaxa: v }))}
                    options={drops.taxasTipos} error={errors.idFreteTaxa} />
                </Col>
                {showValorRegra && (
                  <Col md="2">
                    <Txt label="A Cada" type="number" value={form.valorRegra} onChange={(v) => setForm((p) => ({ ...p, valorRegra: v }))} />
                  </Col>
                )}
                <Col md="2">
                  <Txt label="Valor *" type="number" value={form.valor}
                    onChange={(v) => setForm((p) => {
                      const next = { ...p, valor: v };
                      // Default 98% dos casos: ao começar a digitar Valor, preenche os limites em branco.
                      // Mantém o que o usuário já tenha digitado manualmente.
                      if (v) {
                        if (p.custoMinimo === "" || p.custoMinimo == null) next.custoMinimo = "0";
                        if (p.custoMaximo === "" || p.custoMaximo == null) next.custoMaximo = "999999";
                      }
                      return next;
                    })}
                    error={errors.valor} />
                </Col>
                <Col md="2">
                  <Txt label="Custo Mín." type="number" value={form.custoMinimo} onChange={(v) => setForm((p) => ({ ...p, custoMinimo: v }))} />
                </Col>
                <Col md="2">
                  <Txt label="Custo Máx." type="number" value={form.custoMaximo} onChange={(v) => setForm((p) => ({ ...p, custoMaximo: v }))} />
                </Col>
              </Row>
              <Row>
                <Col md="3">
                  <Sel label="UF Destino" value={form.idEstadoDestino}
                    onChange={(v) => { setForm((p) => ({ ...p, idEstadoDestino: v, idCidadeDestino: "" })); onEstadoChange(v, "taxa"); }}
                    options={estados} placeholder="Todas" />
                </Col>
                <Col md="3">
                  <Sel label="Cidade" value={form.idCidadeDestino}
                    onChange={(v) => {
                      setForm((p) => ({ ...p, idCidadeDestino: v }));
                      if (v) {
                        fetchFaixaCepEnvelopeForCidade(v).then((envelope) => {
                          if (envelope) {
                            setForm((p) => ({ ...p, cepInicial: envelope.cepInicial, cepFinal: envelope.cepFinal }));
                          }
                        });
                      }
                    }}
                    options={cidades.taxa || []} placeholder="Todas" />
                </Col>
                <Col md="3">
                  <Txt label="CEP Inicial" value={form.cepInicial} onChange={(v) => setForm((p) => ({ ...p, cepInicial: formatCepInput(v) }))} placeholder="00000-000" />
                </Col>
                <Col md="3">
                  <Txt label="CEP Final" value={form.cepFinal} onChange={(v) => setForm((p) => ({ ...p, cepFinal: formatCepInput(v) }))} placeholder="99999-999" error={errors.cepFinal} />
                </Col>
              </Row>
              <div style={{ display: "flex", gap: "0.5rem", justifyContent: "flex-end" }}>
                <Button size="sm" color="secondary" onClick={handleCancel}>Cancelar</Button>
                <Button size="sm" color="success" onClick={handleSave} disabled={saving}>
                  {saving ? "Salvando..." : "Salvar"}
                </Button>
              </div>
            </div>
          )}

          {loading ? (
            <div style={{ textAlign: "center", padding: "1rem" }}><ClipLoader size={40} color="var(--theme-accent)" /></div>
          ) : (
            <div style={{ overflowX: "auto", maxHeight: 300 }}>
              <table className="table" style={{ marginBottom: 0 }}>
                <thead>
                  <tr>
                    <th style={thS}>Taxa</th><th style={thS}>Tipo</th><th style={thS}>Por</th>
                    <th style={{ ...thS, textAlign: "right" }}>A Cada</th><th style={{ ...thS, textAlign: "right" }}>Valor</th>
                    <th style={{ ...thS, textAlign: "right" }}>Custo Mín.</th><th style={{ ...thS, textAlign: "right" }}>Custo Máx.</th>
                    <th style={thS}>UF</th><th style={thS}>Cidade</th>
                    <th style={thS}>CEP Ini.</th><th style={thS}>CEP Fim</th>
                    <th style={{ ...thS, width: 70 }}></th>
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 ? (
                    <tr><td colSpan={12} style={{ ...tdS, textAlign: "center", color: "var(--everlog-card-muted)" }}>Nenhuma taxa cadastrada.</td></tr>
                  ) : rows.map((r) => (
                    <tr key={r.id}>
                      <td style={tdS}>{r.freteTaxa}</td>
                      <td style={tdS}>{r.tipo || "-"}</td>
                      <td style={tdS}>{r.por || "-"}</td>
                      <td style={numberCellStyle}>{r.valorRegra != null ? formatNumericLabel(r.valorRegra) : "-"}</td>
                      <td style={numberCellStyle}>{formatTaxaValueLabel(r.valor)}</td>
                      <td style={numberCellStyle}>{r.custoMinimo != null ? formatTaxaValueLabel(r.custoMinimo) : "-"}</td>
                      <td style={numberCellStyle}>{r.custoMaximo != null ? formatTaxaValueLabel(r.custoMaximo) : "-"}</td>
                      <td style={tdS}>{r.uf || "Todos"}</td>
                      <td style={tdS}>{r.cidade || "Todas"}</td>
                      <td style={tdS}>{formatCepLabel(r.cepInicial)}</td>
                      <td style={tdS}>{formatCepLabel(r.cepFinal)}</td>
                      <td style={{ ...tdS, whiteSpace: "nowrap" }}>
                        <button className="btn btn-link btn-sm" style={{ padding: "0 4px", color: "var(--theme-accent)" }}
                          title="Editar" onClick={() => handleEdit(r)}>
                          <i className="tim-icons icon-pencil" />
                        </button>
                        <button className="btn btn-link btn-sm" style={{ padding: "0 4px", color: "var(--everlog-status-danger)" }}
                          title="Excluir" onClick={() => setPendingDeleteId(r.id)}>
                          <i className="tim-icons icon-simple-remove" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: "0.75rem" }}>
            <span style={{ color: "var(--everlog-card-muted)", fontSize: "0.8rem" }}>{total} registro{total !== 1 ? "s" : ""}</span>
            <div style={{ display: "flex", gap: "0.4rem" }}>
              <Button size="sm" color="secondary" disabled={currentPage <= 1} onClick={() => load(offset - LIMIT)}>‹</Button>
              <span style={{ padding: "0.2rem 0.6rem", color: "var(--everlog-card-title)", fontSize: "0.8rem" }}>{currentPage}/{totalPages || 1}</span>
              <Button size="sm" color="secondary" disabled={currentPage >= totalPages} onClick={() => load(offset + LIMIT)}>›</Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ─── Modal principal ──────────────────────────────────────────────────────────

const FORM_EMPTY = {
  nome: "", idFreteUnidade: "", idModal: "", idNegocio: "", idTipoServico: "",
  idTipoVeiculo: "", idTipoFrete: "", idEstadoOrigem: "", idCidadeOrigem: "",
  fatorCubagem: "", dataValIni: "", dataValFim: "",
  servico: "Entrega", status: "true", incideImposto: "true", freteReverso: "false",
};

export default function TabelaFreteModal({ id, onClose }) {
  const history = useHistory();
  const { empresa, transportadora } = useCompanies();
  const empresaId = empresa?.value;
  const transportadoraContextId = transportadora?.value || null;
  const transportadoraContextLabel = transportadora?.label || null;

  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ ...FORM_EMPTY });
  const [savedId, setSavedId] = useState(id || null);
  const [didSaveTabela, setDidSaveTabela] = useState(false);
  const [savedTableMeta, setSavedTableMeta] = useState(null);
  const [aguardandoAprovacao, setAguardandoAprovacao] = useState(false);
  const [impactPreview, setImpactPreview] = useState({ loading: false, total: null, error: "" });
  const [dataErrors, setDataErrors] = useState({});
  const [notice, setNotice] = useState(null);
  // PO1-2859: anexos vindos do GET /frete/tabelas/:id (evita chamada extra na edição).
  const [anexosCarregados, setAnexosCarregados] = useState(undefined);
  const [tabTotals, setTabTotals] = useState({ custos: null, taxas: null });

  const [drops, setDrops] = useState({
    unidades: [], taxasTipos: [], modais: [], tiposServico: [],
    tiposFrete: [], tiposVeiculo: [], negocios: [],
  });

  const [estados, setEstados] = useState([]);
  const [cidades, setCidades] = useState({ origem: [], custo: [], taxa: [] });

  // Carrega dropdowns
  useEffect(() => {
    async function loadDrops() {
      try {
        const [u, tx, m, ts, tf, tv, est, neg] = await Promise.all([
          api.get("/frete/unidades"),
          api.get("/frete/taxas-tipos"),
          api.get("/frete/modais"),
          api.get("/frete/tipos-servico"),
          api.get("/frete/tipos-frete"),
          // Cadastro base + os tipos exclusivos deste cliente (PO1-3489).
          api.get("/frete/tipos-veiculo", empresaId ? { params: { idEmbarcador: empresaId } } : undefined),
          api.get("/frete/estados"),
          empresaId ? api.get(`/embarcadores/${empresaId}/negocios`) : Promise.resolve({ data: [] }),
        ]);
        const toOpt = (arr, vk = "id", lk = "nome") =>
          (Array.isArray(arr) ? arr : []).map((x) => ({ value: String(x[vk]), label: x[lk] }));

        setDrops({
          unidades: toOpt(u.data, "id", "nome").map((o) => ({
            ...o,
            codigo: u.data.find((x) => String(x.id) === o.value)?.codigo || "",
          })),
          taxasTipos: toOpt(tx.data).map((o) => ({
            ...o,
            tipo: tx.data.find((x) => String(x.id) === o.value)?.tipo || "",
            por: tx.data.find((x) => String(x.id) === o.value)?.por || "",
          })),
          modais: toOpt(m.data),
          tiposServico: toOpt(ts.data),
          tiposFrete: toOpt(tf.data),
          tiposVeiculo: toOpt(tv.data),
          negocios: toOpt(neg.data, "id", "descricao"),
        });
        setEstados(toOpt(est.data, "id", "uf"));
      } catch (error) {
        console.error("[TabelaFreteModal] erro ao carregar dropdowns:", error);
        setNotice({ type: "error", message: error.response?.data?.error || error.message || "Erro ao carregar dados auxiliares da tabela." });
      }
    }
    loadDrops();
  }, [empresaId]);

  // Carrega dados se editando
  useEffect(() => {
    if (!id) return;
    setLoading(true);
    api.get(`/frete/tabelas/${id}`).then((res) => {
      const d = res.data;
      setAnexosCarregados(Array.isArray(d.anexos) ? d.anexos : []);
      setForm({
        nome: d.nome || "",
        idFreteUnidade: d.idFreteUnidade != null ? String(d.idFreteUnidade) : "",
        idModal: d.idModal != null ? String(d.idModal) : "",
        idNegocio: d.idNegocio != null ? String(d.idNegocio) : "",
        idTipoServico: d.idTipoServico != null ? String(d.idTipoServico) : "",
        idTipoVeiculo: d.idTipoVeiculo != null ? String(d.idTipoVeiculo) : "",
        idTipoFrete: d.idTipoFrete != null ? String(d.idTipoFrete) : "",
        idEstadoOrigem: d.idEstadoOrigem != null ? String(d.idEstadoOrigem) : "",
        idCidadeOrigem: d.idCidadeOrigem != null ? String(d.idCidadeOrigem) : "",
        fatorCubagem: d.fatorCubagem != null ? String(d.fatorCubagem) : "",
        dataValIni: toDateInputValue(d.dataValIni),
        dataValFim: toDateInputValue(d.dataValFim),
        servico: d.servico || "Entrega",
        status: d.status ? "true" : "false",
        incideImposto: d.incideImposto ? "true" : "false",
        freteReverso: d.freteReverso ? "true" : "false",
      });
      if (d.idEstadoOrigem) loadCidades(String(d.idEstadoOrigem), "origem");
      setAguardandoAprovacao(Boolean(d.aguardandoAprovacao));
      setSavedTableMeta({
        id: d.id,
        nome: d.nome || "",
        dataValIni: d.dataValIni || null,
        dataValFim: d.dataValFim || null,
        idTransportadora: d.idTransportadora || transportadoraContextId,
        transportadoraNome: transportadoraContextLabel,
      });
      setDataErrors({});
    }).catch((error) => {
      console.error("[TabelaFreteModal] erro ao carregar tabela:", error);
      setNotice({ type: "error", message: error.response?.data?.error || error.message || "Erro ao carregar os dados da tabela." });
    }).finally(() => setLoading(false));
  }, [id, transportadoraContextId, transportadoraContextLabel]);

  useEffect(() => {
    if (!didSaveTabela || !savedTableMeta?.id || !empresaId) {
      setImpactPreview({ loading: false, total: null, error: "" });
      return;
    }

    const request = buildRecalculoPreviewRequestFromTable({
      empresaId,
      table: savedTableMeta,
    });

    if (!request || !request.canExecute) {
      setImpactPreview({ loading: false, total: null, error: "" });
      return;
    }

    let active = true;
    setImpactPreview({ loading: true, total: null, error: "" });

    api
      .post(request.previewEndpoint, request.body)
      .then((response) => {
        if (!active) return;
        const total = response?.data?.total;
        const parsed = total == null ? 0 : Number(total);
        setImpactPreview({
          loading: false,
          total: Number.isNaN(parsed) ? 0 : parsed,
          error: "",
        });
      })
      .catch((error) => {
        if (!active) return;
        setImpactPreview({
          loading: false,
          total: null,
          error: error?.response?.data?.error || "Nao foi possivel estimar o impacto inicial.",
        });
      });

    return () => {
      active = false;
    };
  }, [didSaveTabela, empresaId, savedTableMeta]);

  async function loadCidades(idEstado, tipo) {
    if (!idEstado) return;
    const res = await api.get(`/frete/estados/${idEstado}/cidades`);
    const opts = res.data.map((c) => ({ value: String(c.id), label: c.nome }));
    setCidades((prev) => ({ ...prev, [tipo]: opts }));
  }

  function validateDados() {
    const nextErrors = {};

    if (!form.nome) nextErrors.nome = "Informe o nome da tabela.";
    if (!form.idFreteUnidade) nextErrors.idFreteUnidade = "Selecione a unidade.";
    if (!form.idModal) nextErrors.idModal = "Selecione o modal.";
    if (!form.idNegocio) nextErrors.idNegocio = "Selecione o negócio.";
    if (form.dataValIni && parseInt(form.dataValIni.slice(0, 4), 10) > 9999)
      nextErrors.dataValIni = "Ano inválido.";
    if (form.dataValFim && parseInt(form.dataValFim.slice(0, 4), 10) > 9999)
      nextErrors.dataValFim = "Ano inválido.";
    if (form.dataValIni && form.dataValFim && form.dataValFim < form.dataValIni) {
      nextErrors.dataValFim = "A data final deve ser igual ou posterior à data inicial.";
    }

    setDataErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  }

  function handleRecalcularImpactados() {
    const preset = buildRecalculoPresetFromTable(
      savedTableMeta || {
        id: savedId,
        nome: form.nome,
        dataValIni: form.dataValIni || null,
        dataValFim: form.dataValFim || null,
        idTransportadora: transportadoraContextId,
        transportadoraNome: transportadoraContextLabel,
      },
      "modal_tabela",
      { estimativaImpacto: impactPreview.total != null ? impactPreview.total : null }
    );

    if (!preset) return;
    history.push("/app/flow/operacao/recalcular", { recalculoPreset: preset });
  }

  async function handleSave(nextTabAfterSave = false) {
    if (!savedId && !transportadora?.value) {
      setNotice({ type: "danger", message: "Selecione uma transportadora no contexto antes de criar a tabela." });
      return;
    }
    if (!validateDados()) return;
    setSaving(true);
    setNotice(null);
    try {
      const body = {
        ...form,
        idEmbarcador: empresa?.value,
        idTransportadora: transportadora?.value || null,
        idFreteUnidade: Number(form.idFreteUnidade),
        idModal: Number(form.idModal),
        idNegocio: Number(form.idNegocio),
        idTipoServico: form.idTipoServico ? Number(form.idTipoServico) : null,
        idTipoVeiculo: form.idTipoVeiculo ? Number(form.idTipoVeiculo) : null,
        idTipoFrete: form.idTipoFrete ? Number(form.idTipoFrete) : null,
        idEstadoOrigem: form.idEstadoOrigem ? Number(form.idEstadoOrigem) : null,
        idCidadeOrigem: form.idCidadeOrigem ? Number(form.idCidadeOrigem) : null,
        fatorCubagem: form.fatorCubagem ? Number(form.fatorCubagem) : null,
        status: form.status === "true",
        incideImposto: form.incideImposto === "true",
        freteReverso: form.freteReverso === "true",
        dataValIni: form.dataValIni || null,
        dataValFim: form.dataValFim || null,
      };

      let nextSavedId = savedId;

      if (savedId) {
        await api.put(`/frete/tabelas/${savedId}`, body);
        setNotice({ type: "success", message: "Dados da tabela atualizados com sucesso." });
      } else {
        const res = await api.post("/frete/tabelas", body);
        nextSavedId = res.data.id;
        setSavedId(nextSavedId);
        setNotice({ type: "success", message: "Tabela criada com sucesso." });
      }

      setSavedTableMeta({
        id: nextSavedId,
        nome: body.nome,
        dataValIni: body.dataValIni || null,
        dataValFim: body.dataValFim || null,
        idTransportadora: body.idTransportadora || null,
        transportadoraNome: transportadoraContextLabel,
      });
      setDidSaveTabela(true);
      if (nextTabAfterSave) {
        setTab(1);
      }
    } catch (e) {
      setNotice({ type: "error", message: e.response?.data?.error || e.message || "Erro ao salvar tabela." });
    } finally {
      setSaving(false);
    }
  }

  const title = id ? `Editar Tabela — ${form.nome || "..."}` : "Nova Tabela de Frete";
  const tabs = [
    { label: "Dados" },
    { label: `Custos${tabTotals.custos != null ? ` (${tabTotals.custos})` : ""}` },
    { label: `Taxas${tabTotals.taxas != null ? ` (${tabTotals.taxas})` : ""}` },
  ];

  function handleCloseModal() {
    onClose(didSaveTabela);
  }

  return (
    <Modal isOpen toggle={handleCloseModal} size="xl" modalClassName="modal-align-top" fade={false}>
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        background: "var(--everlog-card-surface-flat)", borderBottom: "1px solid var(--everlog-card-border)", padding: "16px 20px",
      }}>
        <span style={{ color: "var(--everlog-card-title)", fontWeight: 600, fontSize: 15 }}>
          <i className="tim-icons icon-notes" style={{ marginRight: 8 }} /> {title}
        </span>
        <button onClick={handleCloseModal} style={{
          background: "none", border: "none", color: "var(--everlog-card-title)",
          fontSize: 22, lineHeight: 1, cursor: "pointer", padding: "0 4px",
        }} aria-label="Fechar">×</button>
      </div>

      {/* Abas */}
      <div style={{ display: "flex", borderBottom: "1px solid var(--everlog-card-border)", background: "var(--everlog-card-surface-flat)" }}>
        {tabs.map((t, i) => (
          <button key={t.label} onClick={() => { if (i === 0 || savedId) setTab(i); }}
            disabled={i > 0 && !savedId}
            style={{
              background: "none", border: "none", borderBottom: tab === i ? "2px solid var(--theme-accent)" : "2px solid transparent",
              color: i > 0 && !savedId ? "var(--everlog-card-text-softer)" : tab === i ? "var(--theme-accent)" : "var(--everlog-card-muted)",
              padding: "10px 20px", cursor: i > 0 && !savedId ? "not-allowed" : "pointer",
              fontSize: "0.88rem", fontWeight: tab === i ? 600 : 400,
            }}>{t.label}</button>
        ))}
      </div>

      <ModalBody style={{ maxHeight: tab === 2 ? "calc(100vh - 126px)" : "calc(100vh - 140px)", overflowY: "auto" }}>
        {loading ? (
          <div style={{ textAlign: "center", padding: "2rem" }}><ClipLoader size={50} color="var(--theme-accent)" /></div>
        ) : (
          <>
            <ContextSummary form={form} drops={drops} />
            <NoticeBanner notice={notice} />
            {didSaveTabela && savedId ? (
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  gap: "0.75rem",
                  flexWrap: "wrap",
                  marginBottom: "1rem",
                  padding: "0.9rem 1rem",
                  borderRadius: 10,
                  border: "1px solid var(--theme-accent-border)",
                  background: "var(--everlog-card-surface-muted)",
                }}
              >
                <div style={{ color: "var(--everlog-card-text)", fontSize: "0.84rem", lineHeight: 1.5 }}>
                  <div>Tabela atualizada. Se a correção afetar documentos já processados, inicie o recálculo agora.</div>
                  <div style={{ marginTop: "0.35rem", color: impactPreview.error ? "var(--everlog-status-danger)" : "var(--theme-accent)", fontSize: "0.8rem" }}>
                    {impactPreview.loading
                      ? "Estimando documentos impactados..."
                      : impactPreview.error
                        ? impactPreview.error
                        : impactPreview.total != null
                          ? `${impactPreview.total} documento(s) podem entrar nesta rodada inicial.`
                          : "A estimativa aparece aqui assim que a tabela for salva."}
                  </div>
                </div>
                <Button
                  color="secondary"
                  size="sm"
                  onClick={handleRecalcularImpactados}
                  style={{
                    background: "linear-gradient(135deg, var(--everlog-status-success-bg), var(--theme-accent-border))",
                    borderColor: "var(--everlog-status-success-border)",
                    color: "var(--everlog-card-title)",
                    fontWeight: 600,
                  }}
                >
                  <i className="tim-icons icon-refresh-01" /> Recalcular impactados{impactPreview.total != null ? ` (${impactPreview.total})` : ""}
                </Button>
              </div>
            ) : null}
            {tab === 0 && (
              <>
                <TabDados form={form} setForm={setForm} drops={drops}
                  estados={estados} cidades={cidades}
                  onEstadoChange={loadCidades} errors={dataErrors}
                  aguardandoAprovacao={aguardandoAprovacao} />
                {/* PO1-2859: anexo original (PDF/planilha/e-mail) da tabela; só depois de existir id */}
                <AnexosTabela
                  tabelaId={savedId}
                  anexosIniciais={savedId && id && String(savedId) === String(id) ? anexosCarregados : undefined}
                  onChange={() => setDidSaveTabela(true)}
                />
                <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.5rem", marginTop: "0.5rem" }}>
                  <Button color="secondary" size="sm" onClick={handleCloseModal}>Cancelar</Button>
                  <Button color="info" size="sm" outline onClick={() => handleSave(false)} disabled={saving}>
                    {saving ? "Salvando..." : "Salvar"}
                  </Button>
                  <Button color="primary" size="sm" onClick={() => handleSave(true)} disabled={saving}>
                    {saving ? "Salvando..." : savedId ? "Salvar e ir para Custos ›" : "Criar e ir para Custos ›"}
                  </Button>
                </div>
              </>
            )}
            {tab === 1 && (
              <TabCustos tabelaId={savedId}
                estados={estados} cidades={cidades} onEstadoChange={loadCidades}
                unidadeCodigo={codigoUnidadeDe(drops, form.idFreteUnidade)}
                onTotalChange={(total) => setTabTotals((prev) => ({ ...prev, custos: total }))} />
            )}
            {tab === 2 && (
              <TabTaxas tabelaId={savedId} drops={drops}
                estados={estados} cidades={cidades} onEstadoChange={loadCidades}
                onTotalChange={(total) => setTabTotals((prev) => ({ ...prev, taxas: total }))} />
            )}
          </>
        )}
      </ModalBody>
    </Modal>
  );
}
